<template>
    <div class="page missions-page">
        <div class="page-header">
            <h1>任務設置</h1>
            <p>以階層式結構管理主線、章節、任務與步驟</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar">
                <a-space class="toolbar-left">
                    <!-- 分類篩選 -->
                    <a-select
                        v-model:value="filterCategoryId"
                        placeholder="全部分類"
                        allow-clear
                        autocomplete="off"
                        style="width: 150px"
                        @change="handleFilterChange"
                    >
                        <a-select-option
                            v-for="category in categories"
                            :key="category.id"
                            :value="category.id"
                        >
                            {{ category.name }}
                        </a-select-option>
                    </a-select>
                    <!-- 分類管理按鈕 -->
                    <a-button
                        v-if="canView"
                        type="text"
                        size="small"
                        @click="showCategoryManagement"
                        class="category-management-btn"
                        title="分類管理"
                    >
                        <template #icon>
                            <SettingOutlined />
                        </template>
                    </a-button>
                </a-space>
                <a-space class="toolbar-right">
                    <a-button
                        v-if="canCreate"
                        type="primary"
                        class="unified-add-btn"
                        @click="showCreateMissionModal"
                    >
                        新增主線
                    </a-button>
                </a-space>
            </div>

            <!-- 主內容區域：左側樹狀選單 + 右側詳情面板 -->
            <div class="main-layout">
                <!-- 左側樹狀選單 -->
                <div
                    class="tree-panel"
                    :style="{ width: leftPanelWidth + 'px' }"
                >
                    <div class="tree-panel-header">
                        <h3>任務階層</h3>
                        <a-space>
                            <a-button
                                v-if="canEdit"
                                type="text"
                                size="small"
                                @click="globalSortingMode = !globalSortingMode"
                                :disabled="loading"
                                :class="{
                                    'sort-mode-active': globalSortingMode
                                }"
                                :title="
                                    globalSortingMode
                                        ? '關閉排序模式'
                                        : '開啟排序模式'
                                "
                            >
                                <template #icon>
                                    <SwapOutlined />
                                </template>
                            </a-button>
                            <a-button
                                type="text"
                                size="small"
                                @click="handleRefresh"
                                :disabled="loading"
                                :loading="loading"
                                title="重新載入"
                            >
                                <template #icon>
                                    <ReloadOutlined />
                                </template>
                            </a-button>
                            <a-button
                                type="text"
                                size="small"
                                @click="collapseAll"
                                :disabled="loading"
                                title="收合全部"
                            >
                                <template #icon>
                                    <ShrinkOutlined />
                                </template>
                            </a-button>
                        </a-space>
                    </div>
                    <div class="tree-panel-content">
                        <div
                            v-if="loading && missions.length === 0"
                            class="loading-container"
                        >
                            <a-spin size="large">
                                <template #indicator>
                                    <div class="custom-spinner">
                                        <div class="spinner-ring"></div>
                                        <div class="spinner-ring"></div>
                                        <div class="spinner-ring"></div>
                                    </div>
                                </template>
                            </a-spin>
                        </div>
                        <div
                            v-else-if="treeData.length === 0"
                            class="empty-container"
                        >
                            <a-empty
                                description="沒有找到任務"
                                :image="false"
                            />
                        </div>
                        <template v-else>
                            <div class="tree-content-wrapper">
                                <a-tree
                                    ref="treeRef"
                                    :tree-data="treeData"
                                    :expanded-keys="expandedKeys"
                                    :selected-keys="selectedKeys"
                                    :field-names="{
                                        title: 'title',
                                        key: 'key',
                                        children: 'children'
                                    }"
                                    block-node
                                    show-line
                                    @expand="handleTreeExpand"
                                    @select="handleTreeSelect"
                                    class="mission-tree"
                                >
                                    <template #title="{ title, dataRef }">
                                        <div
                                            class="tree-node-title"
                                            :data-node-id="dataRef.data?.id"
                                            :data-node-type="dataRef.type"
                                        >
                                            <span
                                                v-if="
                                                    loadingNodes.has(
                                                        dataRef.key
                                                    )
                                                "
                                                class="node-loading"
                                            >
                                                <a-spin size="small" />
                                            </span>
                                            <span class="node-name">
                                                {{ title }}
                                            </span>
                                            <a-tag
                                                v-if="dataRef.categoryName"
                                                :color="dataRef.categoryColor"
                                                class="node-category-tag"
                                            >
                                                {{ dataRef.categoryName }}
                                            </a-tag>
                                            <a-tag
                                                v-if="!dataRef.is_active"
                                                color="red"
                                                class="node-status-tag"
                                            >
                                                停用
                                            </a-tag>
                                            <!-- 排序按鈕區域 -->
                                            <span
                                                v-if="canEdit"
                                                class="node-sort-actions"
                                            >
                                                <!-- 全局排序模式或單個節點排序模式：顯示上下箭頭 -->
                                                <template
                                                    v-if="
                                                        globalSortingMode ||
                                                        sortingNodeKey ===
                                                            dataRef.key
                                                    "
                                                >
                                                    <a-button
                                                        type="text"
                                                        size="small"
                                                        class="sort-btn sort-btn-up"
                                                        @click.stop="
                                                            handleMoveNode(
                                                                dataRef,
                                                                'up'
                                                            )
                                                        "
                                                        title="上移"
                                                    >
                                                        <template #icon>
                                                            <ArrowUpOutlined />
                                                        </template>
                                                    </a-button>
                                                    <a-button
                                                        type="text"
                                                        size="small"
                                                        class="sort-btn sort-btn-down"
                                                        @click.stop="
                                                            handleMoveNode(
                                                                dataRef,
                                                                'down'
                                                            )
                                                        "
                                                        title="下移"
                                                    >
                                                        <template #icon>
                                                            <ArrowDownOutlined />
                                                        </template>
                                                    </a-button>
                                                    <a-button
                                                        v-if="
                                                            !globalSortingMode
                                                        "
                                                        type="text"
                                                        size="small"
                                                        class="sort-btn sort-btn-cancel"
                                                        @click.stop="
                                                            sortingNodeKey =
                                                                null
                                                        "
                                                        title="取消"
                                                    >
                                                        <template #icon>
                                                            <CloseOutlined />
                                                        </template>
                                                    </a-button>
                                                </template>
                                            </span>
                                        </div>
                                    </template>
                                </a-tree>
                            </div>
                            <!-- 分頁組件 -->
                            <div
                                v-if="totalMissions > 0"
                                class="tree-pagination"
                            >
                                <a-pagination
                                    v-model:current="currentPage"
                                    :page-size="20"
                                    :total="totalMissions"
                                    show-quick-jumper
                                    :show-total="
                                        (total, range) =>
                                            `共 ${total} 筆，第 ${range[0]}-${range[1]} 筆`
                                    "
                                    @change="handlePageChange"
                                />
                            </div>
                        </template>
                    </div>
                </div>

                <!-- 拖曳調整寬度的分隔線 -->
                <div
                    class="resizer"
                    @mousedown="startResize"
                    :class="{ resizing: isResizing }"
                ></div>

                <!-- 右側詳情面板 -->
                <div
                    class="detail-panel"
                    :style="{ width: `calc(100% - ${leftPanelWidth + 8}px)` }"
                >
                    <div v-if="!selectedNode" class="detail-panel-empty">
                        <div class="empty-icon">
                            <svg
                                width="120"
                                height="120"
                                viewBox="0 0 120 120"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle
                                    cx="60"
                                    cy="60"
                                    r="50"
                                    stroke="url(#emptyGradient)"
                                    stroke-width="2"
                                    stroke-dasharray="10 5"
                                    opacity="0.4"
                                />
                                <path
                                    d="M45 60 L50 65 L75 40"
                                    stroke="url(#emptyGradient)"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    opacity="0.3"
                                />
                                <defs>
                                    <linearGradient
                                        id="emptyGradient"
                                        x1="0%"
                                        y1="0%"
                                        x2="100%"
                                        y2="100%"
                                    >
                                        <stop
                                            offset="0%"
                                            style="
                                                stop-color: #2a4a7a;
                                                stop-opacity: 1;
                                            "
                                        />
                                        <stop
                                            offset="100%"
                                            style="
                                                stop-color: #3b6ea5;
                                                stop-opacity: 1;
                                            "
                                        />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        <p class="empty-text">請從左側選擇一個項目查看詳情</p>
                    </div>
                    <div v-else class="detail-panel-content">
                        <div class="detail-header">
                            <div class="detail-title">
                                <span
                                    class="detail-icon"
                                    :class="selectedNode.type"
                                >
                                    <FileTextOutlined
                                        v-if="selectedNode.type === 'mission'"
                                    />
                                    <BookOutlined
                                        v-else-if="
                                            selectedNode.type === 'chapter'
                                        "
                                    />
                                    <UnorderedListOutlined
                                        v-else-if="selectedNode.type === 'task'"
                                    />
                                    <CheckCircleOutlined
                                        v-else-if="selectedNode.type === 'step'"
                                    />
                                </span>
                                <h2>{{ selectedNode.name }}</h2>
                                <a-tag
                                    v-if="selectedNode.categoryName"
                                    :color="selectedNode.categoryColor"
                                >
                                    {{ selectedNode.categoryName }}
                                </a-tag>
                                <a-tag
                                    :color="
                                        selectedNode.is_active ? 'green' : 'red'
                                    "
                                >
                                    {{
                                        selectedNode.is_active ? '啟用' : '停用'
                                    }}
                                </a-tag>
                            </div>
                            <a-space class="detail-actions">
                                <a-button
                                    type="default"
                                    @click="handleRefreshDetail"
                                    :loading="refreshingDetail"
                                    title="重新載入詳情"
                                >
                                    <template #icon>
                                        <ReloadOutlined />
                                    </template>
                                </a-button>
                                <a-button
                                    v-if="canEdit"
                                    type="default"
                                    @click="
                                        handleEdit(
                                            selectedNode.data,
                                            selectedNode.type
                                        )
                                    "
                                    title="編輯"
                                >
                                    <template #icon>
                                        <EditOutlined />
                                    </template>
                                </a-button>
                                <a-button
                                    v-if="canCreate"
                                    type="default"
                                    @click="
                                        handleDuplicate(
                                            selectedNode.data,
                                            selectedNode.type
                                        )
                                    "
                                    title="複製"
                                >
                                    <template #icon>
                                        <CopyOutlined />
                                    </template>
                                </a-button>
                                <a-button
                                    v-if="
                                        canCreate &&
                                        selectedNode.type === 'mission'
                                    "
                                    type="primary"
                                    class="unified-add-btn"
                                    @click="
                                        handleAddChapter(selectedNode.data.id)
                                    "
                                >
                                    新增章節
                                </a-button>
                                <a-button
                                    v-if="
                                        canCreate &&
                                        selectedNode.type === 'chapter'
                                    "
                                    type="primary"
                                    class="unified-add-btn"
                                    @click="handleAddTask(selectedNode.data.id)"
                                >
                                    新增任務
                                </a-button>
                                <a-button
                                    v-if="
                                        canCreate &&
                                        selectedNode.type === 'task'
                                    "
                                    type="primary"
                                    class="unified-add-btn"
                                    @click="handleAddStep(selectedNode.data.id)"
                                >
                                    新增步驟
                                </a-button>
                                <a-button
                                    v-if="canDelete"
                                    danger
                                    @click="
                                        handleDelete(
                                            selectedNode.data,
                                            selectedNode.type
                                        )
                                    "
                                    title="刪除"
                                >
                                    <template #icon>
                                        <DeleteOutlined />
                                    </template>
                                </a-button>
                            </a-space>
                        </div>
                        <div class="detail-body">
                            <div class="detail-sections">
                                <!-- 基本資訊 -->
                                <div class="detail-section">
                                    <h3 class="section-header">基本資訊</h3>
                                    <div class="detail-section-content">
                                        <a-row :gutter="16">
                                            <a-col :span="12">
                                                <div class="detail-field">
                                                    <div class="field-label">
                                                        ID
                                                    </div>
                                                    <div class="field-value">
                                                        {{
                                                            selectedNode.data.id
                                                        }}
                                                    </div>
                                                </div>
                                                <div class="detail-field">
                                                    <div class="field-label">
                                                        名稱
                                                    </div>
                                                    <div class="field-value">
                                                        {{
                                                            selectedNode.data
                                                                .name || '-'
                                                        }}
                                                    </div>
                                                </div>
                                                <div class="detail-field">
                                                    <div class="field-label">
                                                        描述
                                                    </div>
                                                    <div class="field-value">
                                                        {{
                                                            selectedNode.data
                                                                .description ||
                                                            '-'
                                                        }}
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.type ===
                                                        'mission'
                                                    "
                                                >
                                                    <div class="field-label">
                                                        分類
                                                    </div>
                                                    <div class="field-value">
                                                        <a-tag
                                                            v-if="
                                                                selectedNode.categoryName
                                                            "
                                                            :color="
                                                                selectedNode.categoryColor
                                                            "
                                                        >
                                                            {{
                                                                selectedNode.categoryName
                                                            }}
                                                        </a-tag>
                                                        <span v-else>-</span>
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .attribute
                                                    "
                                                >
                                                    <div class="field-label">
                                                        屬性
                                                    </div>
                                                    <div class="field-value">
                                                        <a-tag
                                                            :color="
                                                                getAttributeColor(
                                                                    selectedNode
                                                                        .data
                                                                        .attribute
                                                                )
                                                            "
                                                        >
                                                            {{
                                                                getAttributeName(
                                                                    selectedNode
                                                                        .data
                                                                        .attribute
                                                                )
                                                            }}
                                                        </a-tag>
                                                    </div>
                                                </div>
                                            </a-col>
                                            <a-col :span="12">
                                                <div class="detail-field">
                                                    <div class="field-label">
                                                        狀態
                                                    </div>
                                                    <div class="field-value">
                                                        <a-tag
                                                            :color="
                                                                selectedNode
                                                                    .data
                                                                    .is_active
                                                                    ? 'green'
                                                                    : 'red'
                                                            "
                                                        >
                                                            {{
                                                                selectedNode
                                                                    .data
                                                                    .is_active
                                                                    ? '啟用'
                                                                    : '停用'
                                                            }}
                                                        </a-tag>
                                                    </div>
                                                </div>
                                                <div class="detail-field">
                                                    <div class="field-label">
                                                        排序
                                                    </div>
                                                    <div class="field-value">
                                                        {{
                                                            selectedNode.data
                                                                .sequence || '-'
                                                        }}
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .auto_complete !==
                                                        undefined
                                                    "
                                                >
                                                    <div class="field-label">
                                                        達成即完成
                                                    </div>
                                                    <div class="field-value">
                                                        {{
                                                            selectedNode.data
                                                                .auto_complete
                                                                ? '是'
                                                                : '否'
                                                        }}
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .start_time
                                                    "
                                                >
                                                    <div class="field-label">
                                                        開始時間
                                                    </div>
                                                    <div class="field-value">
                                                        {{
                                                            dayjs(
                                                                selectedNode
                                                                    .data
                                                                    .start_time
                                                            ).format(
                                                                'YYYY-MM-DD HH:mm:ss'
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .end_time
                                                    "
                                                >
                                                    <div class="field-label">
                                                        結束時間
                                                    </div>
                                                    <div class="field-value">
                                                        {{
                                                            dayjs(
                                                                selectedNode
                                                                    .data
                                                                    .end_time
                                                            ).format(
                                                                'YYYY-MM-DD HH:mm:ss'
                                                            )
                                                        }}
                                                    </div>
                                                </div>
                                                <template
                                                    v-if="
                                                        selectedNode.type ===
                                                        'mission'
                                                    "
                                                >
                                                    <div class="detail-field">
                                                        <div
                                                            class="field-label"
                                                        >
                                                            章節數量
                                                        </div>
                                                        <div
                                                            class="field-value"
                                                        >
                                                            {{
                                                                Array.isArray(
                                                                    selectedNode
                                                                        .data
                                                                        .chapters
                                                                )
                                                                    ? selectedNode
                                                                          .data
                                                                          .chapters
                                                                          .length
                                                                    : 0
                                                            }}
                                                        </div>
                                                    </div>
                                                </template>
                                                <template
                                                    v-if="
                                                        selectedNode.type ===
                                                        'chapter'
                                                    "
                                                >
                                                    <div class="detail-field">
                                                        <div
                                                            class="field-label"
                                                        >
                                                            任務數量
                                                        </div>
                                                        <div
                                                            class="field-value"
                                                        >
                                                            {{
                                                                Array.isArray(
                                                                    selectedNode
                                                                        .data
                                                                        .tasks
                                                                )
                                                                    ? selectedNode
                                                                          .data
                                                                          .tasks
                                                                          .length
                                                                    : 0
                                                            }}
                                                        </div>
                                                    </div>
                                                </template>
                                                <template
                                                    v-if="
                                                        selectedNode.type ===
                                                        'task'
                                                    "
                                                >
                                                    <div class="detail-field">
                                                        <div
                                                            class="field-label"
                                                        >
                                                            步驟數量
                                                        </div>
                                                        <div
                                                            class="field-value"
                                                        >
                                                            {{
                                                                Array.isArray(
                                                                    selectedNode
                                                                        .data
                                                                        .steps
                                                                )
                                                                    ? selectedNode
                                                                          .data
                                                                          .steps
                                                                          .length
                                                                    : 0
                                                            }}
                                                        </div>
                                                    </div>
                                                </template>
                                            </a-col>
                                        </a-row>
                                    </div>
                                </div>

                                <!-- 可見性規則 -->
                                <div
                                    v-if="
                                        selectedNode.data.visibility_rules &&
                                        selectedNode.data.visibility_rules
                                            .length > 0
                                    "
                                    class="detail-section"
                                >
                                    <div class="section-divider"></div>
                                    <h3 class="section-header">誰可以看到</h3>
                                    <div class="detail-section-content">
                                        <div
                                            v-for="(rule, index) in selectedNode
                                                .data.visibility_rules"
                                            :key="index"
                                            class="rule-item"
                                        >
                                            <a-tag
                                                :color="
                                                    getAudienceTypeColor(
                                                        rule.audience_type
                                                    )
                                                "
                                            >
                                                {{
                                                    getAudienceTypeLabel(
                                                        rule.audience_type
                                                    )
                                                }}
                                            </a-tag>
                                            <span class="rule-ref">
                                                {{
                                                    rule.audience_name ||
                                                    getRuleRefDisplay(
                                                        rule.audience_ref,
                                                        rule.audience_type,
                                                        'visibility'
                                                    ) ||
                                                    rule.audience_ref ||
                                                    '-'
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- 執行規則 -->
                                <div
                                    v-if="
                                        selectedNode.data.executable_rules &&
                                        selectedNode.data.executable_rules
                                            .length > 0
                                    "
                                    class="detail-section"
                                >
                                    <div class="section-divider"></div>
                                    <h3 class="section-header">誰可以執行</h3>
                                    <div class="detail-section-content">
                                        <div
                                            v-for="(rule, index) in selectedNode
                                                .data.executable_rules"
                                            :key="index"
                                            class="rule-item"
                                        >
                                            <a-tag
                                                :color="
                                                    getActorTypeColor(
                                                        rule.actor_type
                                                    )
                                                "
                                            >
                                                {{
                                                    getActorTypeLabel(
                                                        rule.actor_type
                                                    )
                                                }}
                                            </a-tag>
                                            <span class="rule-ref">
                                                {{
                                                    rule.actor_name ||
                                                    getRuleRefDisplay(
                                                        rule.actor_ref,
                                                        rule.actor_type,
                                                        'executable'
                                                    ) ||
                                                    rule.actor_ref ||
                                                    '-'
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <!-- 獎勵設定 -->
                                <div
                                    v-if="
                                        selectedNode.data.rewards &&
                                        selectedNode.data.rewards.length > 0
                                    "
                                    class="detail-section"
                                >
                                    <div class="section-divider"></div>
                                    <h3 class="section-header">獎勵設定</h3>
                                    <div class="detail-section-content">
                                        <div
                                            v-for="(
                                                reward, index
                                            ) in selectedNode.data.rewards"
                                            :key="index"
                                            class="reward-item"
                                        >
                                            <div class="reward-type">
                                                <a-tag color="purple">
                                                    {{
                                                        getRewardTypeLabel(
                                                            reward.reward_type
                                                        )
                                                    }}
                                                </a-tag>
                                            </div>
                                            <div class="reward-details">
                                                <div
                                                    v-if="reward.reference_id"
                                                    class="reward-ref"
                                                >
                                                    {{
                                                        getRewardReferenceLabel(
                                                            reward
                                                        ) || reward.reference_id
                                                    }}
                                                </div>
                                                <div
                                                    v-if="
                                                        reward.value !==
                                                            undefined &&
                                                        reward.value !== null
                                                    "
                                                    class="reward-amount"
                                                >
                                                    數量: {{ reward.value }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- 重複設定 -->
                                <div
                                    v-if="
                                        selectedNode.data.is_repeatable !==
                                        undefined
                                    "
                                    class="detail-section"
                                >
                                    <div class="section-divider"></div>
                                    <h3 class="section-header">重複設定</h3>
                                    <div
                                        class="detail-section-content repeat-settings-inline"
                                    >
                                        <div class="detail-field-inline">
                                            <div class="field-label">
                                                可重複
                                            </div>
                                            <div class="field-value">
                                                <a-tag
                                                    :color="
                                                        selectedNode.data
                                                            .is_repeatable
                                                            ? 'green'
                                                            : 'default'
                                                    "
                                                >
                                                    {{
                                                        selectedNode.data
                                                            .is_repeatable
                                                            ? '是'
                                                            : '否'
                                                    }}
                                                </a-tag>
                                            </div>
                                        </div>
                                        <div
                                            v-if="
                                                selectedNode.data
                                                    .is_repeatable &&
                                                selectedNode.data.repeat_cycle
                                            "
                                            class="detail-field-inline"
                                        >
                                            <div class="field-label">
                                                重複週期
                                            </div>
                                            <div class="field-value">
                                                {{
                                                    getRepeatCycleLabel(
                                                        selectedNode.data
                                                            .repeat_cycle
                                                    )
                                                }}
                                            </div>
                                        </div>
                                        <div
                                            v-if="
                                                selectedNode.data
                                                    .is_repeatable &&
                                                selectedNode.data
                                                    .max_times_per_cycle !==
                                                    undefined
                                            "
                                            class="detail-field-inline"
                                        >
                                            <div class="field-label">
                                                每週期最大次數
                                            </div>
                                            <div class="field-value">
                                                {{
                                                    selectedNode.data
                                                        .max_times_per_cycle ||
                                                    '無限制'
                                                }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- 觸發與前置設定 -->
                                <div
                                    v-if="
                                        selectedNode.data
                                            .unlock_from_node_id !==
                                            undefined ||
                                        selectedNode.data.is_trigger !==
                                            undefined ||
                                        selectedNode.data.triggered_by_id !==
                                            undefined ||
                                        (selectedNode.data.trigger_node_ids &&
                                            selectedNode.data.trigger_node_ids
                                                .length > 0) ||
                                        selectedNode.data.is_required !==
                                            undefined ||
                                        selectedNode.data
                                            .designated_collaborator !==
                                            undefined
                                    "
                                    class="detail-section"
                                >
                                    <div class="section-divider"></div>
                                    <h3 class="section-header">
                                        觸發與前置設定
                                    </h3>
                                    <div class="detail-section-content">
                                        <a-row :gutter="16">
                                            <a-col :span="12">
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .unlock_from_node_id !==
                                                        undefined
                                                    "
                                                >
                                                    <div class="field-label">
                                                        前置任務
                                                    </div>
                                                    <div class="field-value">
                                                        <a-tag color="green">
                                                            {{
                                                                selectedNode
                                                                    .data
                                                                    .unlock_from_node
                                                                    ?.name ||
                                                                selectedNode
                                                                    .data
                                                                    .unlock_from_node_name ||
                                                                getNodeName(
                                                                    selectedNode
                                                                        .data
                                                                        .unlock_from_node_id
                                                                ) ||
                                                                selectedNode
                                                                    .data
                                                                    .unlock_from_node_id ||
                                                                '-'
                                                            }}
                                                        </a-tag>
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .is_required !==
                                                        undefined
                                                    "
                                                >
                                                    <div class="field-label">
                                                        必須完成
                                                    </div>
                                                    <div class="field-value">
                                                        <a-tag
                                                            :color="
                                                                selectedNode
                                                                    .data
                                                                    .is_required
                                                                    ? 'orange'
                                                                    : 'default'
                                                            "
                                                        >
                                                            {{
                                                                selectedNode
                                                                    .data
                                                                    .is_required
                                                                    ? '是'
                                                                    : '否'
                                                            }}
                                                        </a-tag>
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .is_trigger !==
                                                        undefined
                                                    "
                                                >
                                                    <div class="field-label">
                                                        等待觸發
                                                    </div>
                                                    <div class="field-value">
                                                        <a-tag
                                                            :color="
                                                                selectedNode
                                                                    .data
                                                                    .is_trigger
                                                                    ? 'purple'
                                                                    : 'default'
                                                            "
                                                        >
                                                            {{
                                                                selectedNode
                                                                    .data
                                                                    .is_trigger
                                                                    ? '是'
                                                                    : '否'
                                                            }}
                                                        </a-tag>
                                                    </div>
                                                </div>
                                            </a-col>
                                            <a-col :span="12">
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .triggered_by_id !==
                                                        undefined
                                                    "
                                                >
                                                    <div class="field-label">
                                                        被哪個任務觸發
                                                    </div>
                                                    <div class="field-value">
                                                        <a-tag color="purple">
                                                            {{
                                                                selectedNode
                                                                    .data
                                                                    .triggered_by_node
                                                                    ?.name ||
                                                                getNodeName(
                                                                    selectedNode
                                                                        .data
                                                                        .triggered_by_id
                                                                ) ||
                                                                selectedNode
                                                                    .data
                                                                    .triggered_by_id ||
                                                                '-'
                                                            }}
                                                        </a-tag>
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .trigger_node_ids &&
                                                        selectedNode.data
                                                            .trigger_node_ids
                                                            .length > 0
                                                    "
                                                >
                                                    <div class="field-label">
                                                        完成後觸發
                                                    </div>
                                                    <div class="field-value">
                                                        <template
                                                            v-if="
                                                                selectedNode
                                                                    .data
                                                                    .trigger_nodes &&
                                                                selectedNode
                                                                    .data
                                                                    .trigger_nodes
                                                                    .length > 0
                                                            "
                                                        >
                                                            <a-tag
                                                                v-for="(
                                                                    triggerNode,
                                                                    index
                                                                ) in selectedNode
                                                                    .data
                                                                    .trigger_nodes"
                                                                :key="index"
                                                                color="blue"
                                                                style="
                                                                    margin-right: 4px;
                                                                    margin-bottom: 4px;
                                                                "
                                                            >
                                                                {{
                                                                    triggerNode.name ||
                                                                    triggerNode.id
                                                                }}
                                                            </a-tag>
                                                        </template>
                                                        <template v-else>
                                                            <a-tag
                                                                v-for="(
                                                                    nodeId,
                                                                    index
                                                                ) in selectedNode
                                                                    .data
                                                                    .trigger_node_ids"
                                                                :key="index"
                                                                color="blue"
                                                                style="
                                                                    margin-right: 4px;
                                                                    margin-bottom: 4px;
                                                                "
                                                            >
                                                                {{
                                                                    selectedNode
                                                                        .data
                                                                        .trigger_node_names?.[
                                                                        nodeId
                                                                    ] ||
                                                                    getNodeName(
                                                                        nodeId
                                                                    ) ||
                                                                    nodeId
                                                                }}
                                                            </a-tag>
                                                        </template>
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .designated_collaborator !==
                                                        undefined
                                                    "
                                                >
                                                    <div class="field-label">
                                                        協作角色或單位
                                                    </div>
                                                    <div class="field-value">
                                                        <template
                                                            v-if="
                                                                Array.isArray(
                                                                    selectedNode
                                                                        .data
                                                                        .designated_collaborator
                                                                ) &&
                                                                selectedNode
                                                                    .data
                                                                    .designated_collaborator
                                                                    .length > 0
                                                            "
                                                        >
                                                            <a-tag
                                                                v-for="(
                                                                    collaborator,
                                                                    index
                                                                ) in selectedNode
                                                                    .data
                                                                    .designated_collaborator"
                                                                :key="index"
                                                                color="purple"
                                                                style="
                                                                    margin-right: 4px;
                                                                    margin-bottom: 4px;
                                                                "
                                                            >
                                                                {{
                                                                    getCollaboratorLabel(
                                                                        collaborator
                                                                    ) ||
                                                                    collaborator
                                                                }}
                                                            </a-tag>
                                                        </template>
                                                        <template
                                                            v-else-if="
                                                                selectedNode
                                                                    .data
                                                                    .designated_collaborator &&
                                                                !Array.isArray(
                                                                    selectedNode
                                                                        .data
                                                                        .designated_collaborator
                                                                )
                                                            "
                                                        >
                                                            <a-tag
                                                                color="purple"
                                                            >
                                                                {{
                                                                    getCollaboratorLabel(
                                                                        selectedNode
                                                                            .data
                                                                            .designated_collaborator
                                                                    ) ||
                                                                    selectedNode
                                                                        .data
                                                                        .designated_collaborator
                                                                }}
                                                            </a-tag>
                                                        </template>
                                                        <a-tag
                                                            v-else
                                                            color="default"
                                                        >
                                                            -
                                                        </a-tag>
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .is_trigger &&
                                                        selectedNode.data
                                                            .designated_collaborator &&
                                                        selectedNode.data
                                                            .pass_through !==
                                                            undefined
                                                    "
                                                >
                                                    <div class="field-label">
                                                        過期失效
                                                    </div>
                                                    <div class="field-value">
                                                        <a-tag
                                                            :color="
                                                                selectedNode
                                                                    .data
                                                                    .pass_through
                                                                    ? 'orange'
                                                                    : 'default'
                                                            "
                                                        >
                                                            {{
                                                                selectedNode
                                                                    .data
                                                                    .pass_through
                                                                    ? '是'
                                                                    : '否'
                                                            }}
                                                        </a-tag>
                                                    </div>
                                                </div>
                                                <div
                                                    class="detail-field"
                                                    v-if="
                                                        selectedNode.data
                                                            .redirect_url
                                                    "
                                                >
                                                    <div class="field-label">
                                                        跳轉網址
                                                    </div>
                                                    <div class="field-value">
                                                        <a
                                                            :href="
                                                                selectedNode
                                                                    .data
                                                                    .redirect_url
                                                            "
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style="
                                                                color: #3b6ea5;
                                                                text-decoration: underline;
                                                                word-break: break-all;
                                                            "
                                                        >
                                                            {{
                                                                selectedNode
                                                                    .data
                                                                    .redirect_url
                                                            }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </a-col>
                                        </a-row>
                                    </div>
                                </div>

                                <!-- 條件設定 -->
                                <div
                                    v-if="
                                        selectedNode.data.conditions &&
                                        selectedNode.data.conditions.length > 0
                                    "
                                    class="detail-section"
                                >
                                    <div class="section-divider"></div>
                                    <h3 class="section-header">條件設定</h3>
                                    <div class="detail-section-content">
                                        <div
                                            v-for="(
                                                condition, index
                                            ) in selectedNode.data.conditions"
                                            :key="index"
                                            class="rule-item"
                                        >
                                            <div class="rule-ref">
                                                <a-tag
                                                    color="cyan"
                                                    style="margin-right: 8px"
                                                >
                                                    {{
                                                        getConditionTypeLabel(
                                                            condition.type
                                                        ) || condition.type
                                                    }}
                                                </a-tag>
                                                <span
                                                    v-if="condition.code_name"
                                                    style="margin-right: 8px"
                                                >
                                                    條件目標:
                                                    {{ condition.code_name }}
                                                </span>
                                                <span
                                                    v-else-if="condition.code"
                                                    style="margin-right: 8px"
                                                >
                                                    條件目標:
                                                    {{
                                                        getConditionNodeDisplay(
                                                            condition
                                                        )
                                                    }}
                                                </span>
                                                <span
                                                    v-if="condition.operator"
                                                    style="margin: 0 8px"
                                                >
                                                    運算符:
                                                    {{ condition.operator }}
                                                </span>
                                                <span
                                                    v-if="
                                                        condition.required_value
                                                    "
                                                    style="margin-right: 8px"
                                                >
                                                    值:
                                                    {{
                                                        condition.required_value
                                                    }}
                                                </span>
                                                <a-tag
                                                    v-if="
                                                        condition.require_review !==
                                                        undefined
                                                    "
                                                    :color="
                                                        condition.require_review
                                                            ? 'orange'
                                                            : 'default'
                                                    "
                                                    style="margin-left: 8px"
                                                >
                                                    {{
                                                        condition.require_review
                                                            ? '需審核'
                                                            : '不需審核'
                                                    }}
                                                </a-tag>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 新增/編輯主線 Modal -->
        <MissionEditModal
            v-model="editMissionModalVisible"
            v-model:editing-id="editingMissionId"
            :categories="categories"
            :mission-options="missionOptions"
            :confirm-loading="editLoading || editMissionModalLoading"
            @success="handleMissionModalSuccess"
        />

        <!-- 詳情 Modal -->
        <DetailModal
            v-model:visible="detailModalVisible"
            :title="detailModalTitle"
            :data="selectedItem"
        >
            <template v-if="selectedItem">
                <a-descriptions :column="2" bordered>
                    <a-descriptions-item
                        v-for="(value, key) in selectedItem"
                        :key="key"
                        :label="getFieldLabel(key)"
                        :span="key === 'description' ? 2 : 1"
                    >
                        <template v-if="key === 'is_active'">
                            <a-tag :color="value ? 'green' : 'red'">
                                {{ value ? '啟用' : '停用' }}
                            </a-tag>
                        </template>
                        <template v-else-if="key === 'attribute'">
                            <a-tag :color="getAttributeColor(value)">
                                {{ getAttributeName(value) }}
                            </a-tag>
                        </template>
                        <template v-else-if="key === 'executable_rules'">
                            <div v-if="value && value.length > 0">
                                <a-tag
                                    v-for="(rule, index) in value"
                                    :key="index"
                                    style="margin-bottom: 4px"
                                >
                                    {{ getActorTypeLabel(rule.actor_type) }}:
                                    {{
                                        rule.actor_name ||
                                        getRuleRefDisplay(
                                            rule.actor_ref,
                                            rule.actor_type,
                                            'executable'
                                        ) ||
                                        rule.actor_ref ||
                                        '-'
                                    }}
                                </a-tag>
                            </div>
                            <span v-else>-</span>
                        </template>
                        <template v-else-if="key === 'visibility_rules'">
                            <div v-if="value && value.length > 0">
                                <a-tag
                                    v-for="(rule, index) in value"
                                    :key="index"
                                    style="margin-bottom: 4px"
                                >
                                    {{
                                        getAudienceTypeLabel(
                                            rule.audience_type
                                        )
                                    }}:
                                    {{
                                        rule.audience_name ||
                                        getRuleRefDisplay(
                                            rule.audience_ref,
                                            rule.audience_type,
                                            'visibility'
                                        ) ||
                                        rule.audience_ref ||
                                        '-'
                                    }}
                                </a-tag>
                            </div>
                            <span v-else>-</span>
                        </template>
                        <template v-else>
                            {{ value || '-' }}
                        </template>
                    </a-descriptions-item>
                </a-descriptions>
            </template>
        </DetailModal>

        <!-- 新增/編輯章節 Modal -->
        <ChapterEditModal
            v-model="editChapterModalVisible"
            v-model:editing-id="editingChapterId"
            :mission-id="selectedMissionForChapter"
            :mission-options="missionOptions"
            :confirm-loading="editChapterLoading || editChapterModalLoading"
            :mission-time-range="chapterMissionTimeRange"
            :node-name-map="nodeNameMap"
            :unlock-from-node-label="getUnlockFromNodeLabel()"
            :triggered-by-node-label="getTriggeredByNodeLabel()"
            @success="handleChapterModalSuccess"
            @open-node-selector="openChapterNodeSelector"
        />

        <!-- 新增/編輯任務 Modal -->
        <TaskEditModal
            v-model="editTaskModalVisible"
            v-model:editing-id="editingTaskId"
            :chapter-id="selectedChapterForTask"
            :mission-options="missionOptions"
            :confirm-loading="editTaskLoading || editTaskModalLoading"
            :node-name-map="nodeNameMap"
            :unlock-from-node-label="getTaskUnlockFromNodeLabel()"
            :triggered-by-node-label="getTaskTriggeredByNodeLabel()"
            @success="handleTaskModalSuccess"
            @open-node-selector="openTaskNodeSelector"
            @open-condition-node-selector="openNodeSelector"
        />

        <!-- 新增/編輯步驟 Modal -->
        <StepEditModal
            v-model="editStepModalVisible"
            v-model:editing-id="editingStepId"
            :task-id="selectedTaskForStep"
            :mission-options="missionOptions"
            :confirm-loading="editStepLoading || editStepModalLoading"
            :node-name-map="nodeNameMap"
            :unlock-from-node-label="getStepUnlockFromNodeLabel()"
            :triggered-by-node-label="getStepTriggeredByNodeLabel()"
            @success="handleStepModalSuccess"
            @open-node-selector="openStepNodeSelector"
            @open-condition-node-selector="openStepNodeSelectorForCondition"
        />

        <!-- 條件節點選擇器 Modal -->
        <NodeSelectorModal
            v-model:visible="nodeSelectorVisible"
            mode="condition"
            :initial-code="currentConditionRef?.code"
            :missions="missions"
            :mission-hierarchy-cache="missionHierarchyCache"
            :ensure-mission-hierarchy="ensureMissionHierarchy"
            :get-mission-hierarchy-list="getMissionHierarchyList"
            :force-load-task-steps="forceLoadTaskSteps"
            :get-node-hierarchy="getNodeHierarchy"
            @confirm="handleConditionNodeSelectorConfirm"
        />

        <!-- 章節/任務/步驟節點選擇器 Modal -->
        <NodeSelectorModal
            v-model:visible="chapterNodeSelectorVisible"
            :mode="
                chapterNodeSelectorType === 'unlock-source'
                    ? 'unlock'
                    : 'trigger'
            "
            :multiple="chapterNodeSelectorMultiple"
            :initial-node-id="
                chapterNodeSelectorType === 'unlock-source'
                    ? currentModalFormDataRef?.unlock_from_node_id
                    : currentModalFormDataRef?.triggered_by_id
            "
            :initial-node-ids="
                chapterNodeSelectorMultiple
                    ? currentModalFormDataRef?.trigger_node_ids || []
                    : []
            "
            :missions="missions"
            :mission-hierarchy-cache="missionHierarchyCache"
            :ensure-mission-hierarchy="ensureMissionHierarchy"
            :get-mission-hierarchy-list="getMissionHierarchyList"
            :force-load-task-steps="forceLoadTaskSteps"
            :get-node-hierarchy="getNodeHierarchy"
            @confirm="handleChapterNodeSelectorConfirm"
        />

        <!-- 分類管理 Modal -->
        <CategoryManagementModal
            v-model:visible="categoryManagementVisible"
            @success="handleCategoryManagementSuccess"
        />
    </div>
</template>

<script setup>
import {
    ref,
    reactive,
    onMounted,
    computed,
    nextTick,
    onUnmounted,
    watch,
    h
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message, Modal } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    ExpandOutlined,
    ShrinkOutlined,
    PlusOutlined,
    DeleteOutlined,
    FileTextOutlined,
    BookOutlined,
    UnorderedListOutlined,
    CheckCircleOutlined,
    EditOutlined,
    CopyOutlined,
    MoreOutlined,
    EyeOutlined,
    DragOutlined,
    SettingOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined,
    CloseOutlined,
    SwapOutlined,
    ReloadOutlined
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import Sortable from 'sortablejs';
import SearchBar from '@/components/common/SearchBar.vue';
import EditModal from '@/components/common/EditModal.vue';
import DetailModal from '@/components/common/DetailModal.vue';
import MissionEditModal from './components/MissionEditModal.vue';
import ChapterEditModal from './components/ChapterEditModal.vue';
import TaskEditModal from './components/TaskEditModal.vue';
import StepEditModal from './components/StepEditModal.vue';
import NodeSelectorModal from './components/NodeSelectorModal.vue';
import CategoryManagementModal from './components/CategoryManagementModal.vue';
import {
    getMissions,
    getMissionDetail,
    deleteMission,
    duplicateMission,
    copyNode,
    getChapters,
    getTasks,
    getSteps,
    getChapterDetail,
    createChapter,
    updateChapter,
    deleteChapter,
    createTask,
    getTaskDetail,
    updateTask,
    deleteTask,
    createStep,
    getStepDetail,
    updateStep,
    deleteStep,
    enableMission,
    disableMission,
    enableChapter,
    disableChapter,
    enableTask,
    disableTask,
    enableStep,
    disableStep,
    getMissionCategories,
    swapNodeSequence,
    getMissionOptions,
    getNodeHierarchy
} from '@/api/missions';
// searchUsers 已不再使用，因為 Modal 元件已獨立管理
import { handleHttpError } from '@/api/errorHandler';
import { fetchAllPaginatedList } from '@/utils/paginatedFetch';

/** 階層節點列表每頁上限（避免 per_page=1000 一次打爆） */
const HIERARCHY_PAGE_SIZE = 100;

const fetchAllHierarchyRows = async (fetcher, params = {}) => {
    return fetchAllPaginatedList(
        ({ page, per_page }) => fetcher({ ...params, page, per_page }),
        HIERARCHY_PAGE_SIZE
    );
};

// ==================== 共用工具函數 ====================

// 依 sequence 排序的共用函式（sequence 一樣時用 id 當備援）
const sortBySequence = (list = []) => {
    return [...list].sort((a, b) => {
        const aSeq = a.sequence ?? 999999;
        const bSeq = b.sequence ?? 999999;

        if (aSeq !== bSeq) {
            return Number(aSeq) - Number(bSeq);
        }

        const aId = Number(a.id) || 0;
        const bId = Number(b.id) || 0;
        return aId - bId;
    });
};

// 處理時間格式
const formatDateTime = (dateTime) => {
    if (!dateTime) return undefined;
    if (typeof dateTime === 'string') return dateTime;
    if (dateTime.format) {
        return dateTime.format('YYYY-MM-DD HH:mm:ss');
    }
    return undefined;
};

// 處理時間欄位（格式化並移除空值）
// 這些函數已移至 missionFormUtils.js，並由子元件使用

// ==================== 狀態定義 ====================

const loading = ref(false);
const searchText = ref('');
const missions = ref([]);
const expandedItems = ref(new Set()); // 追蹤展開的項目 ID
const missionsContainerRef = ref(null);
let missionsSortableInstance = null;
// missionSelectOptions 已移至 NodeSelectorModal 組件
// 篩選條件
const filterCategoryId = ref(undefined);

// 樹狀選單相關狀態
const treeData = ref([]);
const expandedKeys = ref([]);
const selectedKeys = ref([]);
const selectedNode = ref(null);
const leftPanelWidth = ref(380);
const isResizing = ref(false);
const detailSectionsActiveKeys = ref(['basic']); // 預設展開基本資訊
const refreshingDetail = ref(false); // 重整詳情的載入狀態
const treeRef = ref(null);
let treeSortableInstances = new Map(); // 儲存各層級的 sortable 實例
const loadingNodes = ref(new Set()); // 追蹤正在載入的節點 key
const sortingNodeKey = ref(null); // 追蹤正在排序模式的節點 key
const globalSortingMode = ref(false); // 全局排序模式開關

// 分頁相關狀態
const currentPage = ref(1);
const totalMissions = ref(0);

// 分類列表
const categories = ref([]);

// 分類管理 Modal
const categoryManagementVisible = ref(false);

// 任務選項
const missionOptions = ref({
    rewardTypes: [],
    executable_actor_types: [],
    visibility_audience_types: [],
    repeat_cycles: [],
    attributes: [],
    conditionTypes: [],
    collaboratorTypes: []
});

// Modal 狀態
const editMissionModalVisible = ref(false);
const editChapterModalVisible = ref(false);
const editTaskModalVisible = ref(false);
const editStepModalVisible = ref(false);
const detailModalVisible = ref(false);
const editLoading = ref(false);
const editChapterLoading = ref(false);
const editTaskLoading = ref(false);
const editStepLoading = ref(false);
const editChapterModalLoading = ref(false); // Modal 開啟/關閉時的 loading
const editTaskModalLoading = ref(false);
const editStepModalLoading = ref(false);
const editMissionModalLoading = ref(false);
const selectedItem = ref(null);
const selectedItemType = ref(null); // 'mission', 'chapter', 'task', 'step'
const editingMissionId = ref(null);
const editingChapterId = ref(null);
const editingTaskId = ref(null);
const editingStepId = ref(null);

// 當前章節所屬的 mission 時間範圍
const chapterMissionTimeRange = computed(() => {
    const missionId = selectedMissionForChapter.value;
    if (!missionId) return null;
    const mission = missions.value.find((m) => m.id === missionId);
    if (!mission || (!mission.start_time && !mission.end_time)) return null;

    return {
        start: mission.start_time
            ? dayjs(mission.start_time).format('YYYY-MM-DD HH:mm:ss')
            : null,
        end: mission.end_time
            ? dayjs(mission.end_time).format('YYYY-MM-DD HH:mm:ss')
            : null
    };
});

// 計算屬性
const detailModalTitle = computed(() => {
    const titles = {
        mission: '主線詳情',
        chapter: '章節詳情',
        task: '任務詳情',
        step: '步驟詳情'
    };
    return titles[selectedItemType.value] || '詳情';
});

const getFieldLabel = (key) => {
    const labels = {
        id: 'ID',
        name: '名稱',
        description: '描述',
        sequence: '排序',
        is_active: '狀態',
        attribute: '屬性',
        category_id: '分類ID',
        category: '分類',
        is_repeatable: '可重複',
        repeat_cycle: '重複週期',
        max_times_per_cycle: '每週期最大次數',
        start_time: '開始時間',
        end_time: '結束時間',
        created_at: '建立時間',
        updated_at: '更新時間',
        chapter_count: '章節數量',
        active_chapter_count: '啟用章節數',
        task_count: '任務數量',
        step_count: '步驟數量'
    };
    return labels[key] || key;
};

const getAttributeName = (attribute) => {
    const names = {
        attack: '攻擊',
        defense: '防守',
        support: '支援',
        neutral: '中性'
    };
    return names[attribute] || attribute || '-';
};

const getAttributeColor = (attribute) => {
    const colors = {
        attack: 'red',
        defense: 'blue',
        support: 'green',
        neutral: 'gray'
    };
    return colors[attribute] || 'default';
};

const getActorTypeLabel = (actorType) => {
    const option = missionOptions.value.executable_actor_types.find(
        (opt) => opt.value === actorType
    );
    return option ? option.label : actorType || '-';
};

const getAudienceTypeLabel = (audienceType) => {
    const option = missionOptions.value.visibility_audience_types.find(
        (opt) => opt.value === audienceType
    );
    return option ? option.label : audienceType || '-';
};

const getAudienceTypeColor = (audienceType) => {
    const colors = {
        all: 'blue',
        user: 'green',
        partner: 'orange',
        manager: 'purple'
    };
    return colors[audienceType] || 'default';
};

const getActorTypeColor = (actorType) => {
    const colors = {
        all: 'blue',
        user: 'green',
        partner: 'orange',
        manager: 'purple'
    };
    return colors[actorType] || 'default';
};

const getRewardTypeLabel = (rewardType) => {
    if (rewardType === 'exp') return '經驗值獎勵';
    if (rewardType === 'point') {
        // 可以從 missionOptions 中查找對應的點數類型
        return '點數獎勵';
    }
    return rewardType || '-';
};

// 取得獎勵參考 ID 對應的名稱（用於顯示 reference_id 的名稱）
const getRewardReferenceLabel = (reward) => {
    if (!reward || !reward.reference_id) return null;

    // 從 missionOptions.rewardTypes 中查找對應的選項
    const option = missionOptions.value.rewardTypes.find(
        (opt) => opt.value === reward.reference_id
    );

    if (option) {
        return option.label || option.name || reward.reference_id;
    }

    // 如果找不到，返回原始 ID
    return reward.reference_id;
};

const getRepeatCycleLabel = (cycle) => {
    const cycles = missionOptions.value.repeatCycles || [];
    const option = cycles.find((c) => c.value === cycle);
    return option ? option.label : cycle || '-';
};

// 取得節點名稱
const getNodeName = (nodeId) => {
    if (!nodeId) return null;
    return nodeNameMap.value[nodeId] || null;
};

// 取得協作角色標籤（支援單值或陣列）
const getCollaboratorLabel = (collaborator) => {
    if (!collaborator) return null;

    // 如果是陣列，返回多個標籤（用於顯示）
    if (Array.isArray(collaborator)) {
        return collaborator
            .map((c) => {
                const option = missionOptions.value.collaboratorTypes?.find(
                    (opt) => opt.value === c
                );
                return option ? option.label : c;
            })
            .join(', ');
    }

    // 單值處理
    const option = missionOptions.value.collaboratorTypes?.find(
        (c) => c.value === collaborator
    );
    return option ? option.label : collaborator;
};

// 取得條件類型標籤
const getConditionTypeLabel = (type) => {
    if (!type) return null;
    const conditionType = missionOptions.value.conditionTypes?.find(
        (ct) => ct.type === type
    );
    return conditionType ? conditionType.label : type;
};

// 從條件代碼解析節點 ID（例如：step_1027 -> 1027）
const getNodeIdFromConditionCode = (code) => {
    if (!code) return null;
    const parts = code.split('_');
    if (parts.length >= 2) {
        const nodeId = Number(parts.slice(1).join('_'));
        return nodeId || null;
    }
    return null;
};

// 取得條件節點顯示名稱
const getConditionNodeDisplay = (condition) => {
    if (!condition || !condition.code) return null;

    // 先嘗試從 node_id 獲取名稱
    if (condition.node_id) {
        const nodeName = getNodeName(condition.node_id);
        if (nodeName) {
            return nodeName;
        }
    }

    // 如果沒有 node_id，嘗試從 code 解析
    const nodeId = getNodeIdFromConditionCode(condition.code);
    if (nodeId) {
        const nodeName = getNodeName(nodeId);
        if (nodeName) {
            return nodeName;
        }
    }

    // 如果都找不到，返回 code
    return condition.code;
};

// 取得可見性規則的選項（根據 audience_type）
const getVisibilityTypeOptions = (audienceType) => {
    if (!audienceType) return [];
    const typeConfig = missionOptions.value.visibility_audience_types?.find(
        (type) => type.value === audienceType
    );
    return typeConfig?.options || [];
};

// 取得可執行規則的選項（根據 actor_type）
const getExecutableTypeOptions = (actorType) => {
    if (!actorType) return [];
    const typeConfig = missionOptions.value.executable_actor_types?.find(
        (type) => type.value === actorType
    );
    return typeConfig?.options || [];
};

// 取得規則參考值的顯示標籤（audience_ref 或 actor_ref）
const getRuleRefDisplay = (ref, type, ruleType) => {
    if (!ref) return null;

    // 如果是陣列，處理每個值
    if (Array.isArray(ref)) {
        if (ref.length === 0) return null;
        return ref
            .map((val) => {
                // 根據 ruleType 獲取對應的 options
                const options =
                    ruleType === 'visibility'
                        ? getVisibilityTypeOptions(type)
                        : getExecutableTypeOptions(type);

                // 在 options 中查找對應的選項
                const option = options.find(
                    (opt) =>
                        opt.id === val ||
                        opt.code === val ||
                        opt.value === val ||
                        opt.name === val
                );

                if (option) {
                    return option.label || option.name || val;
                }

                // 如果找不到，返回原始值
                return val;
            })
            .join(', ');
    }

    // 如果是字串（可能是逗號分隔的 ID 列表）
    if (typeof ref === 'string') {
        const refArray = ref.split(',').map((v) => v.trim());
        if (refArray.length === 0) return null;

        const options =
            ruleType === 'visibility'
                ? getVisibilityTypeOptions(type)
                : getExecutableTypeOptions(type);

        return refArray
            .map((val) => {
                // 嘗試轉換為數字（如果是 ID）
                const numVal = Number(val);
                const option = options.find(
                    (opt) =>
                        opt.id === numVal ||
                        opt.id === val ||
                        opt.code === val ||
                        opt.value === val ||
                        opt.name === val
                );

                if (option) {
                    return option.label || option.name || val;
                }

                return val;
            })
            .join(', ');
    }

    // 如果是數字或其他類型
    const options =
        ruleType === 'visibility'
            ? getVisibilityTypeOptions(type)
            : getExecutableTypeOptions(type);

    const option = options.find(
        (opt) =>
            opt.id === ref ||
            opt.code === ref ||
            opt.value === ref ||
            opt.name === ref
    );

    if (option) {
        return option.label || option.name || ref;
    }

    return ref;
};

// 過濾後的主線列表（支援搜尋）
const filteredMissions = computed(() => {
    if (!searchText.value) {
        return missions.value;
    }

    const searchLower = searchText.value.toLowerCase();

    return missions.value
        .map((mission) => {
            // 檢查主線本身是否匹配
            const missionMatches =
                mission.name?.toLowerCase().includes(searchLower) ||
                mission.description?.toLowerCase().includes(searchLower);

            // 遞迴過濾章節、主線、任務和步驟
            const filteredChapters = (mission.chapters || [])
                .map((chapter) => {
                    const chapterMatches =
                        chapter.name?.toLowerCase().includes(searchLower) ||
                        chapter.description
                            ?.toLowerCase()
                            .includes(searchLower);

                    const filteredTasks = (chapter.tasks || [])
                        .map((task) => {
                            const taskMatches =
                                task.name
                                    ?.toLowerCase()
                                    .includes(searchLower) ||
                                task.description
                                    ?.toLowerCase()
                                    .includes(searchLower);

                            const filteredSteps = (task.steps || []).filter(
                                (step) => {
                                    return (
                                        step.name
                                            ?.toLowerCase()
                                            .includes(searchLower) ||
                                        step.description
                                            ?.toLowerCase()
                                            .includes(searchLower)
                                    );
                                }
                            );

                            // 如果任務匹配或有匹配的步驟，保留任務
                            if (taskMatches || filteredSteps.length > 0) {
                                return {
                                    ...task,
                                    steps: filteredSteps
                                };
                            }
                            return null;
                        })
                        .filter(Boolean);

                    // 如果章節匹配或有匹配的任務，保留章節
                    if (chapterMatches || filteredTasks.length > 0) {
                        return {
                            ...chapter,
                            tasks: filteredTasks
                        };
                    }
                    return null;
                })
                .filter(Boolean);

            // 如果任務匹配或有匹配的章節，保留任務
            if (missionMatches || filteredChapters.length > 0) {
                return {
                    ...mission,
                    chapters: filteredChapters
                };
            }
            return null;
        })
        .filter(Boolean);
});

const router = useRouter();
const route = useRoute();
const adminStore = useAdminInfo();

// 權限檢查
const canCreate = computed(() => adminStore.hasPermissionCode('130101')); // 新增任務
const canEdit = computed(() => adminStore.hasPermissionCode('130102')); // 修改任務
const canDelete = computed(() => adminStore.hasPermissionCode('130103')); // 刪除任務
const canView = computed(() => adminStore.hasPermissionCode('130104')); // 查看任務

// 載入主線選項
const loadMissionOptions = async () => {
    try {
        const response = await getMissionOptions();
        if (response.result || response.success) {
            const data = response.data || {};

            // 處理獎勵類型選項
            const rewardTypeOptions = [];
            if (data.reward_types) {
                // 處理經驗值獎勵（單獨一種）
                if (data.reward_types.exp) {
                    rewardTypeOptions.push({
                        value: 'exp',
                        label: data.reward_types.exp, // "經驗值獎勵"
                        type: 'exp'
                    });
                }

                // 處理各種點數類型（CP / XP / PP / ...）
                if (
                    data.reward_types.point &&
                    data.reward_types.point.point_types
                ) {
                    data.reward_types.point.point_types.forEach((pt) => {
                        rewardTypeOptions.push({
                            value: pt.id, // 對應到 reference_id
                            label: pt.code, // 顯示 CP / XP /...
                            code: pt.code,
                            type: 'point'
                        });
                    });
                }
            }

            // 處理執行者類型和受眾類型（直接使用 API 回傳的結構）
            const executableActorTypes = data.executable_actor_types || [];
            const visibilityAudienceTypes =
                data.visibility_audience_types || [];

            // 處理條件類型選項
            const conditionTypes = (data.condition_types || []).map((type) => ({
                ...type,
                options: type.options || []
            }));

            // 處理協作角色類型選項
            const collaboratorTypes = (data.collaborator_types || [])
                .filter((type) => type) // 先只過濾掉完全是 null/undefined 的
                .map((type) => ({
                    label: type.label ?? type.code ?? '未命名角色',
                    value: type.code ?? type.value
                }));

            // 使用 Object.assign 確保響應式更新
            Object.assign(missionOptions.value, {
                rewardTypes: rewardTypeOptions,
                executable_actor_types: executableActorTypes,
                visibility_audience_types: visibilityAudienceTypes,
                repeatCycles: data.repeat_cycles || [],
                attributes: data.attributes || [],
                conditionTypes: conditionTypes,
                collaboratorTypes: collaboratorTypes
            });
        }
    } catch (error) {
        console.error('載入主線選項失敗:', error);
    }
};

const loadCategories = async () => {
    try {
        const response = await getMissionCategories();
        // API 回傳格式: { result: true, message: "成功", data: [...] }
        if (response.result && response.data) {
            categories.value = Array.isArray(response.data)
                ? response.data
                : [];
        } else {
            categories.value = [];
        }
    } catch (error) {
        handleHttpError(error, '載入分類列表');
        categories.value = [];
    }
};

// 方法
const loadMissions = async (page = 1) => {
    try {
        loading.value = true;
        // 切換頁面時不清除 cache，只清除當前頁面的 cache
        if (page !== currentPage.value) {
            // 清除當前頁面的 cache
            missions.value.forEach((mission) => {
                const missionId = normalizeNodeId(mission.id);
                if (missionId !== null) {
                    delete missionHierarchyCache[missionId];
                }
            });
        }

        const params = {
            page: page,
            per_page: 20 // 固定每頁 20 筆
        };

        // 加入篩選參數
        if (filterCategoryId.value !== undefined) {
            params.category_id = filterCategoryId.value;
        }

        const response = await getMissions(params);
        const missionsData = extractListFromResponse(response);

        // 提取分頁資訊
        const pagination =
            response.data?.pagination ||
            response.result?.pagination ||
            response.pagination;
        if (pagination) {
            currentPage.value = pagination.current_page || page;
            totalMissions.value = pagination.total || 0;
        }

        // 只載入主線資料，不載入子資料（懶加載）
        const missionsWithoutHierarchy = missionsData.map((mission) => ({
            ...mission,
            chapters: [], // 不載入章節
            __hierarchyLoaded: false
        }));

        missions.value = missionsWithoutHierarchy;
        missionsWithoutHierarchy.forEach((mission) => {
            setMissionHierarchyCache(mission);
        });

        // 更新樹狀資料
        treeData.value = buildTreeData(missionsWithoutHierarchy);

        // 初始化樹狀選單排序
        nextTick(() => {
            initTreeSortable();
        });
    } catch (error) {
        handleHttpError(error, '載入主線列表');
        showErrorMessage('載入主線列表失敗');
    } finally {
        loading.value = false;
    }
};

// 取得分類名稱
const getCategoryName = (mission) => {
    if (mission.category && typeof mission.category === 'object') {
        return mission.category.name || mission.category;
    }
    if (mission.category && typeof mission.category === 'string') {
        return mission.category;
    }
    // 從 categories 列表中查找
    if (mission.category_id) {
        const category = categories.value.find(
            (c) => c.id === mission.category_id
        );
        if (category) return category.name;
    }
    return null;
};

// 分類標籤顏色（蒸氣波色系、飽和度足以便白字可讀，與分類管理彈窗一致）
const CATEGORY_COLORS = [
    '#2a4a7a', // 霓虹紫
    '#3b6ea5', // 天空藍
    '#e85a9c', // 深粉
    '#64748b', // 日落粉
    '#ffa87a', // 日落橘
    '#6b8ce8', // 深藍
    '#b86ef0', // 紫羅蘭
    '#5eb4c4' // 青藍
];

const getCategoryColor = (categoryId) => {
    if (categoryId == null || categoryId === '') return 'gray';
    const id = Number(categoryId);
    if (Number.isNaN(id)) return 'gray';
    const index = Math.abs(id) % CATEGORY_COLORS.length;
    return CATEGORY_COLORS[index] || 'gray';
};

// 轉換 missions 為樹狀資料結構（每一層都照 sequence 排，支援懶加載）
const buildTreeData = (missionsList) => {
    const sortedMissions = sortBySequence(missionsList || []);

    return sortedMissions.map((mission) => {
        const categoryName = getCategoryName(mission);
        const categoryColor = getCategoryColor(mission.category_id);

        // 如果有 chapters 資料，就使用；否則設為空陣列（但用 isLeaf: false 強制顯示展開按鈕）
        const hasChapters =
            Array.isArray(mission.chapters) && mission.chapters.length > 0;
        const sortedChapters = hasChapters
            ? sortBySequence(mission.chapters)
            : [];

        const missionNode = {
            key: `mission-${mission.id}`,
            title: mission.name,
            type: 'mission',
            is_active: mission.is_active,
            categoryName,
            categoryColor,
            data: mission,
            isLeaf: false, // 強制顯示展開按鈕（mission 總是可以有 chapters）
            // 如果有 chapters 資料，使用實際資料；否則設為空陣列（isLeaf: false 會讓 Tree 顯示展開按鈕）
            children: hasChapters
                ? sortedChapters.map((chapter) => {
                      // 如果有 tasks 資料，就使用；否則設為空陣列（但用 isLeaf: false 強制顯示展開按鈕）
                      const hasTasks =
                          Array.isArray(chapter.tasks) &&
                          chapter.tasks.length > 0;
                      const sortedTasks = hasTasks
                          ? sortBySequence(chapter.tasks)
                          : [];

                      const chapterNode = {
                          key: `chapter-${chapter.id}`,
                          title: chapter.name,
                          type: 'chapter',
                          is_active: chapter.is_active,
                          data: chapter,
                          isLeaf: false, // 強制顯示展開按鈕（chapter 總是可以有 tasks）
                          // 如果有 tasks 資料，使用實際資料；否則設為空陣列（isLeaf: false 會讓 Tree 顯示展開按鈕）
                          children: hasTasks
                              ? sortedTasks.map((task) => {
                                    // 如果有 steps 資料，就使用；否則設為空陣列（但用 isLeaf: false 強制顯示展開按鈕）
                                    const hasSteps =
                                        Array.isArray(task.steps) &&
                                        task.steps.length > 0;
                                    const sortedSteps = hasSteps
                                        ? sortBySequence(task.steps)
                                        : [];

                                    const taskNode = {
                                        key: `task-${task.id}`,
                                        title: task.name,
                                        type: 'task',
                                        is_active: task.is_active,
                                        data: task,
                                        isLeaf: false, // 強制顯示展開按鈕（task 總是可以有 steps）
                                        // 如果有 steps 資料，使用實際資料；否則設為空陣列（isLeaf: false 會讓 Tree 顯示展開按鈕）
                                        children: hasSteps
                                            ? sortedSteps.map((step) => ({
                                                  key: `step-${step.id}`,
                                                  title: step.name,
                                                  type: 'step',
                                                  is_active: step.is_active,
                                                  data: step,
                                                  isLeaf: true // step 是最底層，不需要展開按鈕
                                              }))
                                            : [] // 沒有 steps 資料時設為空陣列（isLeaf: false 會讓 Tree 顯示展開按鈕）
                                    };
                                    return taskNode;
                                })
                              : [] // 沒有 tasks 資料時設為空陣列（isLeaf: false 會讓 Tree 顯示展開按鈕）
                      };
                      return chapterNode;
                  })
                : [] // 沒有 chapters 資料時設為空陣列（isLeaf: false 會讓 Tree 顯示展開按鈕）
        };
        return missionNode;
    });
};

// 取得所有節點的 key（用於展開/收合）
const getAllKeys = (nodes) => {
    const keys = [];
    nodes.forEach((node) => {
        keys.push(node.key);
        if (node.children && node.children.length > 0) {
            keys.push(...getAllKeys(node.children));
        }
    });
    return keys;
};

const expandAll = () => {
    expandedKeys.value = getAllKeys(treeData.value);
    const allIds = new Set();
    missions.value.forEach((mission) => {
        allIds.add(`mission-${mission.id}`);
        mission.chapters?.forEach((chapter) => {
            allIds.add(`chapter-${chapter.id}`);
            chapter.tasks?.forEach((task) => {
                allIds.add(`task-${task.id}`);
            });
        });
    });
    expandedItems.value = allIds;
};

const collapseAll = () => {
    expandedKeys.value = [];
    expandedItems.value.clear();
};

// 重新載入資料
const handleRefresh = async () => {
    // 清除所有已載入的階層資料快取
    missions.value.forEach((mission) => {
        const missionId = normalizeNodeId(mission.id);
        if (missionId !== null) {
            delete missionHierarchyCache[missionId];
        }
    });

    // 收合所有節點
    collapseAll();

    // 清空選中狀態
    selectedKeys.value = [];
    selectedNode.value = null;

    // 重新載入當前頁的資料
    await loadMissions(currentPage.value);
};

// 處理樹狀選單展開（懶加載子資料）
const handleTreeExpand = async (keys) => {
    // 先保存舊的 expandedKeys
    const previousKeys = new Set(expandedKeys.value);

    // 更新 expandedKeys
    expandedKeys.value = keys;

    // 找出新展開的節點（不在之前的 expandedKeys 中）
    const newExpandedKeys = keys.filter((key) => !previousKeys.has(key));

    // 為每個新展開的節點載入子資料
    for (const key of newExpandedKeys) {
        await loadNodeChildren(key);
    }

    // 展開後重新初始化子節點的排序
    nextTick(() => {
        nextTick(() => {
            if (!treeRef.value) return;

            // 獲取 tree 元素的更安全方式
            let treeElement = null;

            // 嘗試從 $el 獲取
            if (treeRef.value.$el) {
                // 檢查是否為元素節點（nodeType === 1）
                if (treeRef.value.$el.nodeType === 1) {
                    treeElement = treeRef.value.$el;
                } else {
                    // 如果是文字節點，嘗試獲取父元素
                    const parent = treeRef.value.$el.parentElement;
                    if (parent && parent.nodeType === 1) {
                        treeElement = parent;
                    }
                }
            }

            // 如果還是沒有，嘗試從 ref 的根元素獲取
            if (!treeElement && treeRef.value.$el) {
                // 查找包含 .ant-tree 的元素
                const treeContainer = document.querySelector('.mission-tree');
                if (treeContainer) {
                    treeElement = treeContainer;
                }
            }

            // 確保 treeElement 是有效的 DOM 元素
            if (
                treeElement &&
                treeElement.nodeType === 1 &&
                typeof treeElement.querySelectorAll === 'function'
            ) {
                setTimeout(() => {
                    initChildSortable(treeElement);
                }, 50);
            } else {
                // 如果找不到有效的元素，直接嘗試從 document 查找
                const fallbackElement = document.querySelector('.mission-tree');
                if (
                    fallbackElement &&
                    fallbackElement.nodeType === 1 &&
                    typeof fallbackElement.querySelectorAll === 'function'
                ) {
                    setTimeout(() => {
                        initChildSortable(fallbackElement);
                    }, 50);
                }
            }
        });
    });
};

// 載入節點的子資料（懶加載）
const loadNodeChildren = async (nodeKey) => {
    try {
        // 設置 loading 狀態
        loadingNodes.value.add(nodeKey);

        // 解析節點 key 獲取類型和 ID
        const match = nodeKey.match(/^(mission|chapter|task)-(\d+)$/);
        if (!match) {
            loadingNodes.value.delete(nodeKey);
            return;
        }

        const [, nodeType, nodeId] = match;
        const id = Number(nodeId);

        switch (nodeType) {
            case 'mission': {
                // 載入章節
                const mission = missions.value.find((m) => m.id === id);
                if (!mission) {
                    loadingNodes.value.delete(nodeKey);
                    return;
                }

                // 檢查是否已經載入過（使用 __hierarchyLoaded 標記，而不是檢查 chapters.length）
                if (
                    mission.__hierarchyLoaded &&
                    Array.isArray(mission.chapters)
                ) {
                    loadingNodes.value.delete(nodeKey);
                    return; // 已經載入過了（即使 chapters 是空陣列）
                }

                const chapters = await fetchAllHierarchyRows(getChapters, {
                    mission_id: id
                });

                // 更新 missions 資料
                const missionIndex = missions.value.findIndex(
                    (m) => m.id === id
                );
                if (missionIndex !== -1) {
                    missions.value[missionIndex] = {
                        ...missions.value[missionIndex],
                        chapters: chapters,
                        __hierarchyLoaded: true
                    };
                    setMissionHierarchyCache(missions.value[missionIndex]);
                }

                // 更新 treeData
                updateTreeData();
                break;
            }
            case 'chapter': {
                // 載入任務
                const mission = missions.value.find(
                    (m) => m.chapters && m.chapters.some((ch) => ch.id === id)
                );
                if (!mission) {
                    loadingNodes.value.delete(nodeKey);
                    return;
                }

                const chapter = mission.chapters.find((ch) => ch.id === id);
                if (!chapter) {
                    loadingNodes.value.delete(nodeKey);
                    return;
                }

                // 檢查是否已經載入過（檢查 tasks 是否已經初始化為陣列）
                // 如果已經載入過，仍然需要更新 treeData 以反映可能的資料變更
                const tasksAlreadyLoaded = Array.isArray(chapter.tasks);

                if (tasksAlreadyLoaded) {
                    // 即使已經載入過，也要更新 treeData（updateTreeData 內部會恢復展開狀態）
                    loadingNodes.value.delete(nodeKey);
                    updateTreeData();
                    return;
                }

                const tasks = await fetchAllHierarchyRows(getTasks, {
                    chapter_id: id
                });

                // 更新 missions 資料
                const missionIndex = missions.value.findIndex(
                    (m) => m.id === mission.id
                );
                if (missionIndex !== -1) {
                    const chapterIndex = missions.value[
                        missionIndex
                    ].chapters.findIndex((ch) => ch.id === id);
                    if (chapterIndex !== -1) {
                        missions.value[missionIndex].chapters[chapterIndex] = {
                            ...missions.value[missionIndex].chapters[
                                chapterIndex
                            ],
                            tasks: tasks
                        };
                        setMissionHierarchyCache(missions.value[missionIndex]);
                    }
                }

                // 更新 treeData
                updateTreeData();
                break;
            }
            case 'task': {
                // 載入步驟
                const mission = missions.value.find(
                    (m) =>
                        m.chapters &&
                        m.chapters.some(
                            (ch) =>
                                ch.tasks && ch.tasks.some((t) => t.id === id)
                        )
                );
                if (!mission) {
                    loadingNodes.value.delete(nodeKey);
                    return;
                }

                const chapter = mission.chapters.find(
                    (ch) => ch.tasks && ch.tasks.some((t) => t.id === id)
                );
                if (!chapter) {
                    loadingNodes.value.delete(nodeKey);
                    return;
                }

                const task = chapter.tasks.find((t) => t.id === id);
                if (!task) {
                    loadingNodes.value.delete(nodeKey);
                    return;
                }

                // 檢查是否已經載入過（檢查 steps 是否已經初始化為陣列）
                if (Array.isArray(task.steps)) {
                    loadingNodes.value.delete(nodeKey);
                    return; // 已經載入過了（即使 steps 是空陣列）
                }

                const steps = await fetchAllHierarchyRows(getSteps, {
                    task_id: id
                });

                // 更新 missions 資料（使用響應式更新）
                const missionIndex = missions.value.findIndex(
                    (m) => m.id === mission.id
                );
                if (missionIndex !== -1) {
                    const chapterIndex = missions.value[
                        missionIndex
                    ].chapters.findIndex((ch) => ch.id === chapter.id);
                    if (chapterIndex !== -1) {
                        const taskIndex = missions.value[missionIndex].chapters[
                            chapterIndex
                        ].tasks.findIndex((t) => t.id === id);
                        if (taskIndex !== -1) {
                            // 使用 Vue 的響應式更新方式
                            const updatedTasks = [
                                ...missions.value[missionIndex].chapters[
                                    chapterIndex
                                ].tasks
                            ];
                            updatedTasks[taskIndex] = {
                                ...updatedTasks[taskIndex],
                                steps: steps
                            };
                            const updatedChapters = [
                                ...missions.value[missionIndex].chapters
                            ];
                            updatedChapters[chapterIndex] = {
                                ...updatedChapters[chapterIndex],
                                tasks: updatedTasks
                            };
                            const updatedMissions = [...missions.value];
                            updatedMissions[missionIndex] = {
                                ...updatedMissions[missionIndex],
                                chapters: updatedChapters
                            };
                            missions.value = updatedMissions;
                            setMissionHierarchyCache(
                                updatedMissions[missionIndex]
                            );
                        }
                    }
                }

                // 更新 treeData
                updateTreeData();
                break;
            }
        }
    } catch (error) {
        console.error(`載入節點 ${nodeKey} 的子資料失敗:`, error);
        handleHttpError(error, '載入子資料');
    } finally {
        // 清除 loading 狀態
        loadingNodes.value.delete(nodeKey);
    }
};

// 更新樹狀資料（重新構建 treeData）
const updateTreeData = () => {
    // 保存當前的 expandedKeys，避免更新後失去展開狀態
    const currentExpandedKeys = [...expandedKeys.value];
    treeData.value = buildTreeData(missions.value);
    // 恢復展開狀態
    nextTick(() => {
        expandedKeys.value = currentExpandedKeys;
    });
};

// 強制重新載入 chapter 的 tasks（用於編輯 task 後更新列表）
const forceReloadChapterTasks = async (chapterId) => {
    try {
        const tasks = await fetchAllHierarchyRows(getTasks, {
            chapter_id: chapterId
        });

        // 更新 missions 資料
        for (const mission of missions.value) {
            const chapterIndex = (mission.chapters || []).findIndex(
                (ch) => ch.id === chapterId
            );
            if (chapterIndex !== -1) {
                const missionIndex = missions.value.findIndex(
                    (m) => m.id === mission.id
                );
                if (missionIndex !== -1) {
                    missions.value[missionIndex].chapters[chapterIndex] = {
                        ...missions.value[missionIndex].chapters[chapterIndex],
                        tasks: tasks
                    };
                    setMissionHierarchyCache(missions.value[missionIndex]);
                }
                break;
            }
        }

        // 不在此處調用 updateTreeData，讓調用者統一處理
        // 這樣可以避免展開狀態被重置
    } catch (error) {
        console.error(`強制重新載入章節 ${chapterId} 的任務失敗:`, error);
        handleHttpError(error, '重新載入任務列表');
    }
};

// 強制重新載入 task 的 steps（用於編輯 step 後更新列表）
const forceReloadTaskSteps = async (taskId) => {
    try {
        const steps = await fetchAllHierarchyRows(getSteps, {
            task_id: taskId
        });

        // 更新 missions 資料
        for (const mission of missions.value) {
            for (const chapter of mission.chapters || []) {
                const taskIndex = (chapter.tasks || []).findIndex(
                    (t) => t.id === taskId
                );
                if (taskIndex !== -1) {
                    const missionIndex = missions.value.findIndex(
                        (m) => m.id === mission.id
                    );
                    if (missionIndex !== -1) {
                        const chapterIndex = missions.value[
                            missionIndex
                        ].chapters.findIndex((ch) => ch.id === chapter.id);
                        if (chapterIndex !== -1) {
                            missions.value[missionIndex].chapters[
                                chapterIndex
                            ].tasks[taskIndex] = {
                                ...missions.value[missionIndex].chapters[
                                    chapterIndex
                                ].tasks[taskIndex],
                                steps: steps
                            };
                            setMissionHierarchyCache(
                                missions.value[missionIndex]
                            );
                        }
                    }
                    break;
                }
            }
        }

        // 不在此處調用 updateTreeData，讓調用者統一處理
        // 這樣可以避免展開狀態被重置
    } catch (error) {
        console.error(`強制重新載入任務 ${taskId} 的步驟失敗:`, error);
        handleHttpError(error, '重新載入步驟列表');
    }
};

// 獲取節點的所有父節點 key（用於確保節點可見）
const getParentKeys = (nodeId, nodeType) => {
    const parentKeys = [];

    if (nodeType === 'mission') {
        // mission 沒有父節點
        return parentKeys;
    }

    // 從 missions 資料中查找節點及其父節點
    for (const mission of missions.value) {
        if (nodeType === 'chapter') {
            const chapter = (mission.chapters || []).find(
                (ch) => ch.id === nodeId
            );
            if (chapter) {
                parentKeys.push(`mission-${mission.id}`);
                return parentKeys;
            }
        } else if (nodeType === 'task') {
            for (const chapter of mission.chapters || []) {
                const task = (chapter.tasks || []).find((t) => t.id === nodeId);
                if (task) {
                    parentKeys.push(`mission-${mission.id}`);
                    parentKeys.push(`chapter-${chapter.id}`);
                    return parentKeys;
                }
            }
        } else if (nodeType === 'step') {
            for (const chapter of mission.chapters || []) {
                for (const task of chapter.tasks || []) {
                    const step = (task.steps || []).find(
                        (s) => s.id === nodeId
                    );
                    if (step) {
                        parentKeys.push(`mission-${mission.id}`);
                        parentKeys.push(`chapter-${chapter.id}`);
                        parentKeys.push(`task-${task.id}`);
                        return parentKeys;
                    }
                }
            }
        }
    }

    return parentKeys;
};

// 處理樹狀選單選擇
const handleTreeSelect = async (selectedKeysValue, info) => {
    if (selectedKeysValue.length === 0) {
        selectedKeys.value = [];
        selectedNode.value = null;
        return;
    }

    const key = selectedKeysValue[0];
    selectedKeys.value = selectedKeysValue;

    // 從 treeData 中找到對應的節點
    const findNode = (nodes, targetKey) => {
        for (const node of nodes) {
            if (node.key === targetKey) {
                return node;
            }
            if (node.children && node.children.length > 0) {
                const found = findNode(node.children, targetKey);
                if (found) return found;
            }
        }
        return null;
    };

    const node = findNode(treeData.value, key);
    if (node && node.data && node.data.id) {
        // 先使用現有資料顯示
        selectedNode.value = {
            key: node.key,
            name: node.title,
            type: node.type,
            is_active:
                node.data.is_active !== undefined
                    ? node.data.is_active
                    : node.is_active,
            categoryName: node.categoryName,
            categoryColor: node.categoryColor,
            data: node.data
        };

        // 從 API 載入完整資料
        try {
            let fullData = null;
            switch (node.type) {
                case 'mission':
                    // Mission 類型也需要載入完整資料，確保包含 visibility_rules 和 executable_rules
                    const missionResponse = await getMissionDetail(
                        node.data.id
                    );
                    fullData =
                        missionResponse.data?.data ||
                        missionResponse.data ||
                        missionResponse.result?.data;
                    break;
                case 'chapter':
                    const chapterResponse = await getChapterDetail(
                        node.data.id
                    );
                    fullData =
                        chapterResponse.data?.data ||
                        chapterResponse.data ||
                        chapterResponse.result?.data;
                    break;
                case 'task':
                    const taskResponse = await getTaskDetail(node.data.id);
                    fullData =
                        taskResponse.data?.data ||
                        taskResponse.data ||
                        taskResponse.result?.data;
                    break;
                case 'step':
                    const stepResponse = await getStepDetail(node.data.id);
                    fullData =
                        stepResponse.data?.data ||
                        stepResponse.data ||
                        stepResponse.result?.data;
                    break;
            }

            // 如果有完整資料，更新 selectedNode
            if (fullData) {
                selectedNode.value = {
                    ...selectedNode.value,
                    is_active:
                        fullData.is_active !== undefined
                            ? fullData.is_active
                            : selectedNode.value.is_active,
                    data: fullData
                };
            }
        } catch (error) {
            console.error('載入節點詳情失敗:', error);
            // 載入失敗時仍使用現有資料
        }
    }
};

// 重整詳情面板內容
const handleRefreshDetail = async () => {
    if (!selectedNode.value || !selectedNode.value.data) {
        return;
    }

    try {
        refreshingDetail.value = true;
        const node = selectedNode.value;
        let fullData = null;

        switch (node.type) {
            case 'mission':
                const missionResponse = await getMissionDetail(node.data.id);
                fullData =
                    missionResponse.data?.data ||
                    missionResponse.data ||
                    missionResponse.result?.data;
                break;
            case 'chapter':
                const chapterResponse = await getChapterDetail(node.data.id);
                fullData =
                    chapterResponse.data?.data ||
                    chapterResponse.data ||
                    chapterResponse.result?.data;
                break;
            case 'task':
                const taskResponse = await getTaskDetail(node.data.id);
                fullData =
                    taskResponse.data?.data ||
                    taskResponse.data ||
                    taskResponse.result?.data;
                break;
            case 'step':
                const stepResponse = await getStepDetail(node.data.id);
                fullData =
                    stepResponse.data?.data ||
                    stepResponse.data ||
                    stepResponse.result?.data;
                break;
        }

        // 更新 selectedNode 的資料
        if (fullData) {
            selectedNode.value = {
                ...selectedNode.value,
                is_active:
                    fullData.is_active !== undefined
                        ? fullData.is_active
                        : selectedNode.value.is_active,
                data: fullData
            };
        }
    } catch (error) {
        console.error('重整詳情失敗:', error);
        handleHttpError(error, '重整詳情');
        showErrorMessage('重整詳情失敗');
    } finally {
        refreshingDetail.value = false;
    }
};

// 拖曳調整寬度
const startResize = (e) => {
    isResizing.value = true;
    const startX = e.clientX;
    const startWidth = leftPanelWidth.value;

    const doResize = (moveEvent) => {
        const diff = moveEvent.clientX - startX;
        const newWidth = Math.max(250, Math.min(600, startWidth + diff));
        leftPanelWidth.value = newWidth;
    };

    const stopResize = () => {
        isResizing.value = false;
        document.removeEventListener('mousemove', doResize);
        document.removeEventListener('mouseup', stopResize);
    };

    document.addEventListener('mousemove', doResize);
    document.addEventListener('mouseup', stopResize);
};

// 初始化樹狀選單拖曳排序
const initTreeSortable = () => {
    // 如果沒有編輯權限，不初始化排序功能
    if (!canEdit.value) {
        return;
    }

    // 清除所有現有的 sortable 實例
    treeSortableInstances.forEach((instance) => {
        if (instance) instance.destroy();
    });
    treeSortableInstances.clear();

    // 使用雙重 nextTick 確保 DOM 完全渲染
    nextTick(() => {
        nextTick(() => {
            if (!treeRef.value) return;

            // 獲取樹狀選單的 DOM
            const treeElement = treeRef.value.$el;
            if (!treeElement) return;

            // 等待更長時間確保 Ant Design Tree 完全渲染
            setTimeout(() => {
                // 為所有層級初始化排序
                initAllLevelSortable(treeElement);
            }, 300);
        });
    });
};

// 初始化所有層級的排序
const initAllLevelSortable = (treeElement) => {
    // 檢查 treeElement 是否為有效的 DOM 元素
    if (!treeElement || typeof treeElement.querySelector !== 'function') {
        console.warn(
            '[initAllLevelSortable] treeElement 不是有效的 DOM 元素',
            treeElement
        );
        return;
    }

    // Mission 層級（根層級）
    const rootContainer = treeElement.querySelector(
        '.ant-tree-list-holder-inner'
    );
    if (rootContainer) {
        const rootNodes = rootContainer.querySelectorAll(
            '> .ant-tree-treenode'
        );
        if (rootNodes.length > 1) {
            // 只有多於一個節點時才需要排序
            try {
                // 檢查是否已經有實例
                if (treeSortableInstances.has('root')) {
                    treeSortableInstances.get('root').destroy();
                }

                // 檢查拖曳手柄是否存在
                const hasDragHandle =
                    rootContainer.querySelector('.drag-handle-icon');
                if (!hasDragHandle) {
                    console.warn('找不到拖曳手柄元素');
                }

                // 使用 handle 選項來限制拖曳
                const instance = Sortable.create(rootContainer, {
                    animation: 150,
                    ghostClass: 'sortable-ghost',
                    handle: '.drag-handle-icon',
                    forceFallback: false, // 先嘗試不使用 fallback
                    fallbackOnBody: false,
                    draggable: '.ant-tree-treenode', // 指定可拖曳的元素
                    // 移除 filter，因為可能會阻止拖曳
                    chosenClass: 'sortable-chosen',
                    dragClass: 'sortable-drag',
                    onStart: (evt) => {
                        evt.item.style.opacity = '0.5';
                    },
                    onMove: (evt) => {
                        // 確保只能在同一個容器內移動
                        if (evt.from !== evt.to) {
                            return false; // 跨容器移動，不允許
                        }
                        return true; // 允許移動
                    },
                    onEnd: async (evt) => {
                        evt.item.style.opacity = '1';
                        // 確保移動是在同一個容器內
                        if (evt.from === evt.to) {
                            await handleTreeSortByNode('mission', evt);
                        } else {
                            // 跨容器移動，恢復原狀
                            evt.from.insertBefore(
                                evt.item,
                                evt.from.children[evt.oldIndex]
                            );
                            message.warning('只能在同層級內排序');
                        }
                    }
                });
                treeSortableInstances.set('root', instance);
            } catch (error) {
                console.error('初始化根層級排序失敗:', error);
            }
        }
    }

    // 遞迴處理所有子層級
    initChildSortable(treeElement);
};

// 遞迴初始化子節點的排序
const initChildSortable = (parentElement) => {
    // 檢查 parentElement 是否為有效的 DOM 元素
    if (
        !parentElement ||
        typeof parentElement.querySelectorAll !== 'function'
    ) {
        console.warn(
            '[initChildSortable] parentElement 不是有效的 DOM 元素',
            parentElement
        );
        return;
    }

    // 找到所有包含子節點的容器
    const allChildContainers = parentElement.querySelectorAll(
        '.ant-tree-child-tree .ant-tree-list-holder-inner'
    );

    allChildContainers.forEach((container, index) => {
        const nodes = container.querySelectorAll('> .ant-tree-treenode');
        if (nodes.length > 1) {
            // 只有多於一個節點時才需要排序
            // 判斷節點類型 - 從 data-node-type 屬性獲取
            const firstNode = nodes[0];
            const nodeTitle = firstNode.querySelector('.tree-node-title');
            if (nodeTitle) {
                // 優先從 data-node-type 屬性獲取類型
                const nodeType = nodeTitle.getAttribute('data-node-type');
                let type = nodeType || 'chapter'; // 預設

                // 使用更穩定的 instanceKey（不包含時間戳）
                const instanceKey = `${type}-${index}`;

                // 如果已經有實例，先銷毀
                if (treeSortableInstances.has(instanceKey)) {
                    treeSortableInstances.get(instanceKey).destroy();
                }

                // 檢查拖曳手柄是否存在
                const hasDragHandle =
                    container.querySelector('.drag-handle-icon');
                if (!hasDragHandle) {
                    console.warn(`找不到拖曳手柄元素: ${instanceKey}`);
                }

                try {
                    // 使用 handle 選項來限制拖曳
                    const instance = Sortable.create(container, {
                        animation: 150,
                        ghostClass: 'sortable-ghost',
                        handle: '.drag-handle-icon',
                        forceFallback: false, // 先嘗試不使用 fallback
                        fallbackOnBody: false,
                        draggable: '.ant-tree-treenode', // 指定可拖曳的元素
                        // 移除 filter，因為可能會阻止拖曳
                        chosenClass: 'sortable-chosen',
                        dragClass: 'sortable-drag',
                        onStart: (evt) => {
                            evt.item.style.opacity = '0.5';
                        },
                        onMove: (evt) => {
                            // 確保只能在同一個容器內移動
                            if (evt.from !== evt.to) {
                                return false; // 跨容器移動，不允許
                            }

                            // 檢查節點類型是否一致
                            const draggedType = evt.dragged
                                .querySelector('.tree-node-title')
                                ?.getAttribute('data-node-type');
                            const relatedType = evt.related
                                .querySelector('.tree-node-title')
                                ?.getAttribute('data-node-type');

                            // 必須類型一致，且符合當前層級類型
                            if (
                                draggedType !== relatedType ||
                                draggedType !== type
                            ) {
                                return false; // 類型不一致，不允許
                            }

                            return true; // 允許移動
                        },
                        onEnd: async (evt) => {
                            evt.item.style.opacity = '1';
                            // 確保移動是在同一個容器內，且類型一致
                            if (evt.from === evt.to) {
                                const draggedType = evt.item
                                    .querySelector('.tree-node-title')
                                    ?.getAttribute('data-node-type');
                                if (draggedType === type) {
                                    await handleTreeSortByNode(type, evt);
                                } else {
                                    // 類型不一致，恢復原狀
                                    evt.from.insertBefore(
                                        evt.item,
                                        evt.from.children[evt.oldIndex]
                                    );
                                    message.warning('只能在同層級內排序');
                                }
                            } else {
                                // 跨容器移動，恢復原狀
                                evt.from.insertBefore(
                                    evt.item,
                                    evt.from.children[evt.oldIndex]
                                );
                                message.warning('只能在同層級內排序');
                            }
                        }
                    });
                    treeSortableInstances.set(instanceKey, instance);
                } catch (error) {
                    console.error(`初始化 ${type} 層級排序失敗:`, error);
                }
            }
        }
    });
};

// 處理節點移動（上下移動）
const handleMoveNode = async (nodeData, direction) => {
    try {
        const nodeId = nodeData.data?.id;
        const nodeType = nodeData.type;
        if (!nodeId || !nodeType) {
            message.warning('無法取得節點資訊');
            return;
        }

        // 找到當前節點在同層級中的位置
        let parentNode = null;
        let siblings = [];
        let currentIndex = -1;

        // 根據節點類型找到父節點和兄弟節點
        if (nodeType === 'mission') {
            siblings = missions.value;
            currentIndex = siblings.findIndex((m) => m.id === nodeId);
        } else if (nodeType === 'chapter') {
            for (const mission of missions.value) {
                const chapterIndex = (mission.chapters || []).findIndex(
                    (ch) => ch.id === nodeId
                );
                if (chapterIndex !== -1) {
                    parentNode = mission;
                    siblings = mission.chapters || [];
                    currentIndex = chapterIndex;
                    break;
                }
            }
        } else if (nodeType === 'task') {
            for (const mission of missions.value) {
                for (const chapter of mission.chapters || []) {
                    const taskIndex = (chapter.tasks || []).findIndex(
                        (t) => t.id === nodeId
                    );
                    if (taskIndex !== -1) {
                        parentNode = chapter;
                        siblings = chapter.tasks || [];
                        currentIndex = taskIndex;
                        break;
                    }
                }
                if (currentIndex !== -1) break;
            }
        } else if (nodeType === 'step') {
            for (const mission of missions.value) {
                for (const chapter of mission.chapters || []) {
                    for (const task of chapter.tasks || []) {
                        const stepIndex = (task.steps || []).findIndex(
                            (s) => s.id === nodeId
                        );
                        if (stepIndex !== -1) {
                            parentNode = task;
                            siblings = task.steps || [];
                            currentIndex = stepIndex;
                            break;
                        }
                    }
                    if (currentIndex !== -1) break;
                }
                if (currentIndex !== -1) break;
            }
        }

        if (currentIndex === -1) {
            message.warning('找不到節點位置');
            return;
        }

        // 計算目標位置
        let targetIndex = -1;
        if (direction === 'up') {
            if (currentIndex === 0) {
                message.info('已經是最上方');
                return;
            }
            targetIndex = currentIndex - 1;
        } else if (direction === 'down') {
            if (currentIndex === siblings.length - 1) {
                message.info('已經是最下方');
                return;
            }
            targetIndex = currentIndex + 1;
        }

        if (targetIndex === -1 || !siblings[targetIndex]) {
            message.warning('無法移動到目標位置');
            return;
        }

        // 取得目標節點 ID
        const targetId = siblings[targetIndex].id;

        // 保存當前的展開狀態
        const savedExpandedKeys = [...expandedKeys.value];

        // 找出需要保持展開的父節點 key
        let parentKey = null;
        if (nodeType === 'chapter') {
            // chapter 的父節點是 mission
            if (parentNode) {
                parentKey = `mission-${parentNode.id}`;
            }
        } else if (nodeType === 'task') {
            // task 的父節點是 chapter
            if (parentNode) {
                parentKey = `chapter-${parentNode.id}`;
                // 同時需要展開 mission
                for (const mission of missions.value) {
                    if (
                        mission.chapters &&
                        mission.chapters.some((ch) => ch.id === parentNode.id)
                    ) {
                        const missionKey = `mission-${mission.id}`;
                        if (!savedExpandedKeys.includes(missionKey)) {
                            savedExpandedKeys.push(missionKey);
                        }
                        break;
                    }
                }
            }
        } else if (nodeType === 'step') {
            // step 的父節點是 task
            if (parentNode) {
                parentKey = `task-${parentNode.id}`;
                // 同時需要展開 mission 和 chapter
                for (const mission of missions.value) {
                    for (const chapter of mission.chapters || []) {
                        if (
                            chapter.tasks &&
                            chapter.tasks.some((t) => t.id === parentNode.id)
                        ) {
                            const missionKey = `mission-${mission.id}`;
                            const chapterKey = `chapter-${chapter.id}`;
                            if (!savedExpandedKeys.includes(missionKey)) {
                                savedExpandedKeys.push(missionKey);
                            }
                            if (!savedExpandedKeys.includes(chapterKey)) {
                                savedExpandedKeys.push(chapterKey);
                            }
                            break;
                        }
                    }
                }
            }
        }

        // 確保父節點在展開列表中
        if (parentKey && !savedExpandedKeys.includes(parentKey)) {
            savedExpandedKeys.push(parentKey);
        }

        // 呼叫 API 交換順序
        await swapNodeSequence(nodeType, nodeId, targetId);
        message.success('排序更新成功');

        // 重新載入資料（保持當前頁碼）
        await loadMissions(currentPage.value);

        // 恢復展開狀態
        nextTick(() => {
            expandedKeys.value = savedExpandedKeys;

            // 確保所有展開的節點都被載入子資料
            const loadPromises = savedExpandedKeys.map((key) =>
                loadNodeChildren(key)
            );
            Promise.all(loadPromises).then(() => {
                // 重新初始化排序
                nextTick(() => {
                    initTreeSortable();
                });
            });
        });

        // 保持排序模式開啟（不關閉）
        // sortingNodeKey 保持不變
    } catch (error) {
        handleHttpError(error, '更新排序');
        showErrorMessage('排序更新失敗');
    }
};

// 處理樹狀選單排序（根據節點資料）
const handleTreeSortByNode = async (type, evt) => {
    const { oldIndex, newIndex } = evt;
    if (oldIndex === newIndex) return;

    try {
        // 驗證是否在同一個容器內
        if (evt.from !== evt.to) {
            console.warn('跨容器移動，不允許');
            return;
        }

        // 先把當前容器內的 treenode 抓出來（只要 .ant-tree-treenode）
        const allNodes = Array.from(evt.to.children).filter((el) =>
            el.classList.contains('ant-tree-treenode')
        );

        if (!allNodes[oldIndex] || !allNodes[newIndex] || !evt.item) {
            console.warn('找不到對應的 treenode，無法排序');
            return;
        }

        // 被拖曳的元素（evt.item）
        const draggedEl = evt.item;
        const draggedTitle = draggedEl.querySelector('.tree-node-title');
        const draggedId = draggedTitle
            ? Number(draggedTitle.getAttribute('data-node-id'))
            : NaN;
        const draggedType = draggedTitle
            ? draggedTitle.getAttribute('data-node-type')
            : null;

        // 驗證類型是否一致
        if (draggedType !== type) {
            console.warn('節點類型不一致，不允許排序', {
                draggedType,
                expectedType: type
            });
            return;
        }

        // 要跟它交換 sequence 的對象：原本 oldIndex 位置上的那個節點
        const targetEl =
            allNodes[oldIndex] === draggedEl
                ? allNodes[newIndex]
                : allNodes[oldIndex];

        const targetTitle = targetEl.querySelector('.tree-node-title');
        const targetId = targetTitle
            ? Number(targetTitle.getAttribute('data-node-id'))
            : NaN;
        const targetType = targetTitle
            ? targetTitle.getAttribute('data-node-type')
            : null;

        // 驗證目標節點類型是否一致
        if (targetType !== type) {
            console.warn('目標節點類型不一致，不允許排序', {
                targetType,
                expectedType: type
            });
            return;
        }

        if (!draggedId || !targetId || isNaN(draggedId) || isNaN(targetId)) {
            console.warn('無法從 DOM 取得節點 id', {
                draggedId,
                targetId,
                type
            });
            // 直接 reload 回復原狀（保持當前頁碼）
            await loadMissions(currentPage.value);
            return;
        }

        // 立刻把 DOM 順序改回去，交給 Vue + data 重畫
        evt.from.insertBefore(evt.item, evt.from.children[oldIndex]);

        // 呼叫後端 API 交換 sequence
        await swapNodeSequence(type, draggedId, targetId);
        message.success('排序更新成功');

        // 重載資料，讓 sequence 排序生效（保持當前頁碼）
        await loadMissions(currentPage.value);
    } catch (error) {
        handleHttpError(error, '更新排序');
        showErrorMessage('排序更新失敗');
        await loadMissions(currentPage.value);
    }
};

const toggleExpand = (itemId) => {
    if (expandedItems.value.has(itemId)) {
        expandedItems.value.delete(itemId);
    } else {
        expandedItems.value.add(itemId);
    }
};

const showCreateMissionModal = async () => {
    // 清空編輯 ID 並打開 Modal
    editingMissionId.value = null;
    editMissionModalVisible.value = true;
};

// 主線 Modal 成功回調
const handleMissionModalSuccess = async () => {
    // 保存當前的展開狀態和選中狀態
    const savedExpandedKeys = [...expandedKeys.value];
    const savedSelectedKeys = [...selectedKeys.value];
    // 保存編輯的節點 ID（如果有）
    const editedNodeId = editingMissionId.value;
    const editedNodeType = 'mission';
    // 保存當前選中的節點（用於刷新詳情）
    const currentSelectedNode = selectedNode.value;

    await loadMissions(currentPage.value);

    // 恢復展開狀態和選中狀態
    nextTick(() => {
        // 獲取被編輯節點的所有父節點 keys
        const parentKeys = editedNodeId
            ? getParentKeys(editedNodeId, editedNodeType)
            : [];

        // 合併保存的展開狀態和父節點 keys
        const allExpandedKeys = [
            ...new Set([...savedExpandedKeys, ...parentKeys])
        ];

        expandedKeys.value = allExpandedKeys;
        selectedKeys.value = savedSelectedKeys;

        // 確保所有展開的節點都被載入子資料
        const loadPromises = allExpandedKeys.map((key) =>
            loadNodeChildren(key)
        );
        Promise.all(loadPromises).then(() => {
            // 更新 treeData
            updateTreeData();

            // 如果之前有選中的節點，刷新詳情
            if (currentSelectedNode && currentSelectedNode.data) {
                const nodeKey = `${currentSelectedNode.type}-${currentSelectedNode.data.id}`;
                if (savedSelectedKeys.includes(nodeKey)) {
                    handleRefreshDetail();
                }
            }

            // 重新初始化排序
            nextTick(() => {
                initTreeSortable();
            });
        });
    });
};

// 章節 Modal 成功後重新載入資料
const handleChapterModalSuccess = async () => {
    // 保存當前的展開狀態和選中狀態
    const savedExpandedKeys = [...expandedKeys.value];
    const savedSelectedKeys = [...selectedKeys.value];
    // 保存編輯的節點 ID 或新增時的父節點 ID
    const editedNodeId = editingChapterId.value;
    const editedNodeType = 'chapter';
    // 如果是新增，需要確保父 mission 被展開
    const parentMissionId = selectedMissionForChapter.value;
    // 保存當前選中的節點（用於刷新詳情）
    const currentSelectedNode = selectedNode.value;

    await loadMissions(currentPage.value);

    // 恢復展開狀態和選中狀態
    nextTick(() => {
        // 獲取被編輯節點的所有父節點 keys
        const parentKeys = editedNodeId
            ? getParentKeys(editedNodeId, editedNodeType)
            : parentMissionId
              ? [`mission-${parentMissionId}`]
              : [];

        // 合併保存的展開狀態和父節點 keys
        const allExpandedKeys = [
            ...new Set([...savedExpandedKeys, ...parentKeys])
        ];

        expandedKeys.value = allExpandedKeys;
        selectedKeys.value = savedSelectedKeys;

        // 確保所有展開的節點都被載入子資料
        const loadPromises = allExpandedKeys.map((key) =>
            loadNodeChildren(key)
        );
        Promise.all(loadPromises).then(() => {
            // 更新 treeData
            updateTreeData();

            // 如果之前有選中的節點，刷新詳情
            if (currentSelectedNode && currentSelectedNode.data) {
                const nodeKey = `${currentSelectedNode.type}-${currentSelectedNode.data.id}`;
                if (savedSelectedKeys.includes(nodeKey)) {
                    handleRefreshDetail();
                }
            }

            // 重新初始化排序
            nextTick(() => {
                initTreeSortable();
            });
        });
    });
};

// 任務 Modal 成功後重新載入資料
const handleTaskModalSuccess = async () => {
    // 保存當前的展開狀態和選中狀態
    const savedExpandedKeys = [...expandedKeys.value];
    const savedSelectedKeys = [...selectedKeys.value];
    // 保存編輯的節點 ID 或新增時的父節點 ID
    const editedNodeId = editingTaskId.value;
    const parentChapterId = selectedChapterForTask.value;
    // 保存當前選中的節點（用於刷新詳情）
    const currentSelectedNode = selectedNode.value;

    // 獲取需要重新載入的 chapter ID
    const chapterIdToReload = editedNodeId
        ? selectedChapterForTask.value
        : parentChapterId;

    // 清除該 chapter 的已載入 tasks 資料，強制重新載入
    if (chapterIdToReload) {
        for (const mission of missions.value) {
            const chapterIndex = (mission.chapters || []).findIndex(
                (ch) => ch.id === chapterIdToReload
            );
            if (chapterIndex !== -1) {
                // 清除 tasks 資料，強制重新載入
                const missionIndex = missions.value.findIndex(
                    (m) => m.id === mission.id
                );
                if (missionIndex !== -1) {
                    missions.value[missionIndex].chapters[chapterIndex] = {
                        ...missions.value[missionIndex].chapters[chapterIndex],
                        tasks: undefined // 清除 tasks，下次展開時會重新載入
                    };
                    setMissionHierarchyCache(missions.value[missionIndex]);
                }
                break;
            }
        }
    }

    // 更新 treeData
    updateTreeData();

    // 恢復展開狀態和選中狀態
    nextTick(() => {
        // 恢復展開狀態（保持之前打開的節點）
        expandedKeys.value = savedExpandedKeys;
        selectedKeys.value = savedSelectedKeys;

        // 確保所有展開的節點都被載入子資料
        const loadPromises = savedExpandedKeys.map((key) =>
            loadNodeChildren(key)
        );
        Promise.all(loadPromises).then(() => {
            // 更新 treeData
            updateTreeData();

            // 如果之前有選中的節點，刷新詳情
            if (currentSelectedNode && currentSelectedNode.data) {
                const nodeKey = `${currentSelectedNode.type}-${currentSelectedNode.data.id}`;
                if (savedSelectedKeys.includes(nodeKey)) {
                    handleRefreshDetail();
                }
            }

            // 重新初始化排序
            nextTick(() => {
                initTreeSortable();
            });
        });
    });
};

// 步驟 Modal 成功後重新載入資料
const handleStepModalSuccess = async () => {
    // 保存當前的展開狀態和選中狀態
    const savedExpandedKeys = [...expandedKeys.value];
    const savedSelectedKeys = [...selectedKeys.value];
    // 保存編輯的節點 ID 或新增時的父節點 ID
    const editedNodeId = editingStepId.value;
    const parentTaskId = selectedTaskForStep.value;
    // 保存當前選中的節點（用於刷新詳情）
    const currentSelectedNode = selectedNode.value;

    // 獲取需要重新載入的 task ID
    const taskIdToReload = editedNodeId
        ? selectedTaskForStep.value
        : parentTaskId;

    // 清除該 task 的已載入 steps 資料，強制重新載入
    if (taskIdToReload) {
        for (const mission of missions.value) {
            for (const chapter of mission.chapters || []) {
                const taskIndex = (chapter.tasks || []).findIndex(
                    (t) => t.id === taskIdToReload
                );
                if (taskIndex !== -1) {
                    // 清除 steps 資料，強制重新載入
                    const missionIndex = missions.value.findIndex(
                        (m) => m.id === mission.id
                    );
                    if (missionIndex !== -1) {
                        const chapterIndex = missions.value[
                            missionIndex
                        ].chapters.findIndex((ch) => ch.id === chapter.id);
                        if (chapterIndex !== -1) {
                            missions.value[missionIndex].chapters[
                                chapterIndex
                            ].tasks[taskIndex] = {
                                ...missions.value[missionIndex].chapters[
                                    chapterIndex
                                ].tasks[taskIndex],
                                steps: undefined // 清除 steps，下次展開時會重新載入
                            };
                            setMissionHierarchyCache(
                                missions.value[missionIndex]
                            );
                        }
                    }
                    break;
                }
            }
        }
    }

    // 更新 treeData
    updateTreeData();

    // 恢復展開狀態和選中狀態
    nextTick(() => {
        // 恢復展開狀態（保持之前打開的節點）
        expandedKeys.value = savedExpandedKeys;
        selectedKeys.value = savedSelectedKeys;

        // 確保所有展開的節點都被載入子資料
        const loadPromises = savedExpandedKeys.map((key) =>
            loadNodeChildren(key)
        );
        Promise.all(loadPromises).then(() => {
            // 更新 treeData
            updateTreeData();

            // 如果之前有選中的節點，刷新詳情
            if (currentSelectedNode && currentSelectedNode.data) {
                const nodeKey = `${currentSelectedNode.type}-${currentSelectedNode.data.id}`;
                if (savedSelectedKeys.includes(nodeKey)) {
                    handleRefreshDetail();
                }
            }

            // 重新初始化排序
            nextTick(() => {
                initTreeSortable();
            });
        });
    });
};

// 章節需要的 computed 屬性：編輯時從 API 資料獲取，新增時使用臨時存儲
const selectedMissionForChapter = computed(() => {
    if (editingChapterId.value) {
        // 編輯模式：從 missions 資料中查找
        for (const mission of missions.value) {
            const chapter = (mission.chapters || []).find(
                (ch) => ch.id === editingChapterId.value
            );
            if (chapter) {
                return mission.id;
            }
        }
        return null;
    }
    // 新增模式：使用臨時存儲
    return newChapterMissionId.value;
});

// 任務需要的 computed 屬性：編輯時從 API 資料獲取，新增時使用臨時存儲
const selectedChapterForTask = computed(() => {
    if (editingTaskId.value) {
        // 編輯模式：從 missions 資料中查找
        for (const mission of missions.value) {
            for (const chapter of mission.chapters || []) {
                const task = (chapter.tasks || []).find(
                    (t) => t.id === editingTaskId.value
                );
                if (task) {
                    return chapter.id;
                }
            }
        }
        return null;
    }
    // 新增模式：使用臨時存儲
    return newTaskChapterId.value;
});

// 步驟需要的 computed 屬性：編輯時從 API 資料獲取，新增時使用臨時存儲
const selectedTaskForStep = computed(() => {
    if (editingStepId.value) {
        // 編輯模式：從 missions 資料中查找
        for (const mission of missions.value) {
            for (const chapter of mission.chapters || []) {
                for (const task of chapter.tasks || []) {
                    const step = (task.steps || []).find(
                        (s) => s.id === editingStepId.value
                    );
                    if (step) {
                        return task.id;
                    }
                }
            }
        }
        return null;
    }
    // 新增模式：使用臨時存儲
    return newStepTaskId.value;
});

// 節點名稱映射
const nodeNameMap = computed(() => {
    const map = {};
    missions.value.forEach((mission) => {
        map[mission.id] = mission.name;
        if (mission.chapters) {
            mission.chapters.forEach((chapter) => {
                map[chapter.id] = chapter.name;
                if (chapter.tasks) {
                    chapter.tasks.forEach((task) => {
                        map[task.id] = task.name;
                        if (task.steps) {
                            task.steps.forEach((step) => {
                                map[step.id] = step.name;
                            });
                        }
                    });
                }
            });
        }
    });
    return map;
});

// 節點選擇器相關狀態
const nodeSelectorVisible = ref(false);
const currentConditionRef = ref(null); // 當前選擇的條件（用於條件節點選擇器）

// 章節節點選擇器相關狀態（解鎖/觸發用）
const chapterNodeSelectorVisible = ref(false);
const chapterNodeSelectorType = ref(null); // 'unlock-source' 或 'trigger'
const chapterNodeSelectorMultiple = ref(false); // 是否多選
const currentModalFormDataRef = ref(null); // 當前 Modal 的表單資料

// 正規化節點 ID（字串化），避免不同型別造成比較失敗
const normalizeNodeId = (value) => {
    if (value === undefined || value === null || value === '') return null;
    return String(value);
};

const missionHierarchyCache = reactive({});

const setMissionHierarchyCache = (mission) => {
    if (!mission) return;
    const missionId = normalizeNodeId(mission.id);
    if (missionId === null) return;
    missionHierarchyCache[missionId] = mission;
};

const getMissionHierarchyList = () => Object.values(missionHierarchyCache);

const getMissionFromHierarchyCache = (missionId) => {
    const normalizedId = normalizeNodeId(missionId);
    if (normalizedId === null) return null;

    // 優先從 cache 取得
    if (missionHierarchyCache[normalizedId]) {
        const cachedMission = missionHierarchyCache[normalizedId];
        // 確保 cache 中的 mission 有完整的階層資料
        if (
            cachedMission?.__hierarchyLoaded &&
            Array.isArray(cachedMission.chapters)
        ) {
            return cachedMission;
        }
    }

    // 從 missions.value 找
    const mission =
        missions.value.find((m) => normalizeNodeId(m.id) === normalizedId) ||
        null;

    if (mission) {
        // 確保 mission 有 chapters 陣列（即使為空）
        if (!Array.isArray(mission.chapters)) {
            mission.chapters = [];
        }
        // 如果 mission 有完整的階層資料，更新 cache
        if (mission.__hierarchyLoaded && Array.isArray(mission.chapters)) {
            setMissionHierarchyCache(mission);
        }
    }

    return mission;
};

const extractListFromResponse = (response) => {
    if (!response) return [];
    const candidates = [
        response.data?.data,
        response.data?.items,
        response.data,
        response.result?.data,
        response.result,
        response
    ];
    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            return candidate;
        }
    }
    return [];
};

const shouldLoadChildren = (count) => {
    if (count === undefined || count === null) return true;
    const numericValue = Number(count);
    if (Number.isNaN(numericValue)) return true;
    return numericValue > 0;
};

const buildTaskHierarchy = async (task) => {
    if (!task || task.id === undefined || task.id === null) {
        return {
            ...(task || {}),
            steps: Array.isArray(task?.steps) ? task.steps : []
        };
    }

    const hasSteps = Array.isArray(task.steps) && task.steps.length > 0;
    if (hasSteps) {
        return {
            ...task,
            steps: task.steps
        };
    }

    if (!shouldLoadChildren(task.step_count)) {
        return {
            ...task,
            steps: []
        };
    }

    try {
        const steps = await fetchAllHierarchyRows(getSteps, {
            task_id: task.id
        });
        return {
            ...task,
            steps
        };
    } catch (error) {
        console.error(`載入任務 ${task.id} 的步驟失敗:`, error);
        return {
            ...task,
            steps: []
        };
    }
};

const buildChapterHierarchy = async (chapter) => {
    if (!chapter || chapter.id === undefined || chapter.id === null) {
        return {
            ...(chapter || {}),
            tasks: Array.isArray(chapter?.tasks) ? chapter.tasks : []
        };
    }

    const hasTasks = Array.isArray(chapter.tasks) && chapter.tasks.length > 0;
    if (hasTasks) {
        const tasksWithSteps = await Promise.all(
            chapter.tasks.map((task) => buildTaskHierarchy(task))
        );
        return {
            ...chapter,
            tasks: tasksWithSteps
        };
    }

    if (!shouldLoadChildren(chapter.task_count)) {
        return {
            ...chapter,
            tasks: []
        };
    }

    try {
        const tasks = await fetchAllHierarchyRows(getTasks, {
            chapter_id: chapter.id
        });
        const tasksWithSteps = await Promise.all(
            tasks.map((task) => buildTaskHierarchy(task))
        );
        return {
            ...chapter,
            tasks: tasksWithSteps
        };
    } catch (error) {
        console.error(`載入章節 ${chapter.id} 的任務失敗:`, error);
        return {
            ...chapter,
            tasks: []
        };
    }
};

const buildMissionHierarchy = async (mission) => {
    if (!mission || mission.id === undefined || mission.id === null) {
        return {
            ...(mission || {}),
            chapters: Array.isArray(mission?.chapters) ? mission.chapters : [],
            __hierarchyLoaded: false
        };
    }

    const hasChapters =
        Array.isArray(mission.chapters) && mission.chapters.length > 0;
    if (hasChapters) {
        const hydratedChapters = await Promise.all(
            mission.chapters.map((chapter) => buildChapterHierarchy(chapter))
        );
        return {
            ...mission,
            chapters: hydratedChapters,
            __hierarchyLoaded: true
        };
    }

    try {
        const chapters = await fetchAllHierarchyRows(getChapters, {
            mission_id: mission.id
        });
        const hydratedChapters = await Promise.all(
            chapters.map((chapter) => buildChapterHierarchy(chapter))
        );
        return {
            ...mission,
            chapters: hydratedChapters,
            __hierarchyLoaded: true
        };
    } catch (error) {
        console.error(`載入主線 ${mission.id} 的章節失敗:`, error);
        return {
            ...mission,
            chapters: [],
            __hierarchyLoaded: false
        };
    }
};

const missionHierarchyLoadingMap = new Map();

const ensureMissionHierarchy = async (missionId) => {
    const normalizedMissionId = normalizeNodeId(missionId);
    if (normalizedMissionId === null) return;

    if (missionHierarchyLoadingMap.has(normalizedMissionId)) {
        return missionHierarchyLoadingMap.get(normalizedMissionId);
    }

    const missionIndex = missions.value.findIndex(
        (mission) => normalizeNodeId(mission.id) === normalizedMissionId
    );

    if (missionIndex === -1) return;

    const mission = missions.value[missionIndex];

    // 如果已經有完整的階層資料，直接更新 cache 並返回
    if (
        mission?.__hierarchyLoaded &&
        Array.isArray(mission.chapters) &&
        mission.chapters.length > 0
    ) {
        setMissionHierarchyCache(mission);
        return;
    }

    const loadPromise = (async () => {
        const missionWithHierarchy = await buildMissionHierarchy(mission);
        const updatedMissions = [...missions.value];
        updatedMissions[missionIndex] = missionWithHierarchy;
        missions.value = updatedMissions;
        setMissionHierarchyCache(missionWithHierarchy);
    })();

    missionHierarchyLoadingMap.set(normalizedMissionId, loadPromise);

    try {
        await loadPromise;
    } catch (error) {
        console.error(`補載主線 ${missionId} 階層失敗:`, error);
    } finally {
        missionHierarchyLoadingMap.delete(normalizedMissionId);
    }
};

// 強制載入任務的 steps（用於 NodeSelectorModal）
const forceLoadTaskSteps = async (taskId) => {
    try {
        const steps = await fetchAllHierarchyRows(getSteps, {
            task_id: taskId
        });

        // 更新 missions.value 中對應的 task.steps
        for (const mission of missions.value) {
            for (const chapter of mission.chapters || []) {
                for (const task of chapter.tasks || []) {
                    if (normalizeNodeId(task.id) === normalizeNodeId(taskId)) {
                        task.steps = steps;
                        // 更新 cache
                        setMissionHierarchyCache(mission);
                        return steps;
                    }
                }
            }
        }
        return steps;
    } catch (error) {
        console.error(`強制載入任務 ${taskId} 的步驟失敗:`, error);
        return [];
    }
};

// 這些 watch 和函數已移至 NodeSelectorModal 組件

// 此函數已不再使用，因為 Modal 元件已獨立管理

// currentConditionRef 已在上面定義

// 開啟節點選擇器（條件模式）
const openNodeSelector = (conditionIndex, condition) => {
    currentConditionRef.value = condition;
    nodeSelectorVisible.value = true;
};

// 處理條件節點選擇器確認
const handleConditionNodeSelectorConfirm = ({ code, nodeId, nodeName }) => {
    if (currentConditionRef.value) {
        const condition = currentConditionRef.value;

        // 強制設置為 node_complete 類型
        condition.type = 'node_complete';

        // 強制寫入 node_id（優先使用 nodeId，否則從 code 解析）
        if (nodeId) {
            condition.node_id = Number(nodeId);
        } else if (code) {
            const parts = code.split('_');
            if (parts.length >= 2) {
                const parsedNodeId = Number(parts.slice(1).join('_'));
                if (parsedNodeId) {
                    condition.node_id = parsedNodeId;
                }
            }
        }

        // 設置 code（用於相容後端格式）
        if (condition.node_id) {
            condition.code = `node_complete_${condition.node_id}`;
        } else if (code) {
            condition.code = code;
        }

        // 保存節點名稱（用於顯示）
        if (nodeName) {
            condition.node_name = nodeName;
        }
    }
    nodeSelectorVisible.value = false;
    currentConditionRef.value = null;
};

// 這些 computed 和函數已移至 NodeSelectorModal 組件

// 這些函數已移至 NodeSelectorModal 組件

// 開啟章節節點選擇器（同時支援章節和任務）
const openChapterNodeSelector = (type, multiple, formData = null) => {
    chapterNodeSelectorType.value = type;
    chapterNodeSelectorMultiple.value = multiple;
    currentModalFormDataRef.value = formData;
    chapterNodeSelectorVisible.value = true;
};

// 處理章節節點選擇器確認
const handleChapterNodeSelectorConfirm = ({
    nodeId,
    nodeIds,
    nodeName,
    nodeNames
}) => {
    if (!currentModalFormDataRef.value) {
        console.warn(
            '[MissionsList] handleChapterNodeSelectorConfirm: no formData ref'
        );
        return;
    }

    if (chapterNodeSelectorMultiple.value && nodeIds) {
        // 多選模式
        currentModalFormDataRef.value.trigger_node_ids = nodeIds;
        // 更新觸發節點的名稱映射（用於顯示）
        if (nodeNames && typeof nodeNames === 'object') {
            currentModalFormDataRef.value.trigger_node_names = { ...nodeNames };
        }
    } else if (!chapterNodeSelectorMultiple.value && nodeId) {
        // 單選模式
        if (chapterNodeSelectorType.value === 'trigger') {
            currentModalFormDataRef.value.triggered_by_id = nodeId;
            // 更新觸發節點的名稱（用於顯示）
            if (nodeName) {
                currentModalFormDataRef.value.triggered_by_node_name = nodeName;
            }
        } else if (chapterNodeSelectorType.value === 'unlock-source') {
            currentModalFormDataRef.value.unlock_from_node_id = nodeId;
            // 更新解鎖來源節點的名稱（用於顯示）
            if (nodeName) {
                currentModalFormDataRef.value.unlock_from_node_name = nodeName;
            }
        }
    }

    chapterNodeSelectorVisible.value = false;
    currentModalFormDataRef.value = null;
};

// 取得觸發節點的標籤（單選）
// 注意：此函數已不再使用，因為 ChapterEditModal 自己管理表單資料
// 保留此函數是為了向後兼容，但返回空字串，讓 Modal 元件自己計算標籤
const getTriggeredByNodeLabel = () => {
    return '';
};

// 取得解鎖來源節點的標籤（單選）
// 注意：此函數已不再使用，因為 ChapterEditModal 自己管理表單資料
// 保留此函數是為了向後兼容，但返回空字串，讓 Modal 元件自己計算標籤
const getUnlockFromNodeLabel = () => {
    return '';
};

// 這些函數已移至 NodeSelectorModal 組件

// 任務節點選擇器（重用章節的節點選擇器）
const openTaskNodeSelector = (type, multiple, formData = null) => {
    // 重用章節的節點選擇器邏輯
    openChapterNodeSelector(type, multiple, formData);
};

// 這些函數已移至 NodeSelectorModal 組件

// 取得任務解鎖來源節點的標籤
// 注意：此函數已不再使用，因為 TaskEditModal 自己管理表單資料
// 保留此函數是為了向後兼容，但返回空字串，讓 Modal 元件自己計算標籤
const getTaskUnlockFromNodeLabel = () => {
    return '';
};

// 取得任務觸發節點的標籤
// 注意：此函數已不再使用，因為 TaskEditModal 自己管理表單資料
// 保留此函數是為了向後兼容，但返回空字串，讓 Modal 元件自己計算標籤
const getTaskTriggeredByNodeLabel = () => {
    return '';
};

// 取得步驟解鎖來源節點的標籤
// 注意：此函數已不再使用，因為 StepEditModal 自己管理表單資料
// 保留此函數是為了向後兼容，但返回空字串，讓 Modal 元件自己計算標籤
const getStepUnlockFromNodeLabel = () => {
    return '';
};

// 取得步驟觸發節點的標籤
// 注意：此函數已不再使用，因為 StepEditModal 自己管理表單資料
// 保留此函數是為了向後兼容，但返回空字串，讓 Modal 元件自己計算標籤
const getStepTriggeredByNodeLabel = () => {
    return '';
};

// 步驟節點選擇器（重用章節的節點選擇器）
const openStepNodeSelector = (type, multiple, formData = null) => {
    // 重用章節的節點選擇器邏輯
    openChapterNodeSelector(type, multiple, formData);
};

// 步驟條件節點選擇器（重用節點選擇器邏輯）
const openStepNodeSelectorForCondition = (conditionIndex, condition) => {
    // 重用節點選擇器邏輯
    openNodeSelector(conditionIndex, condition);
};

// handleViewDetail 已不再使用，詳情現在在右側面板顯示

const handleEdit = async (item, type) => {
    try {
        if (type === 'mission') {
            editingMissionId.value = item.id;
            editMissionModalVisible.value = true;
        } else if (type === 'chapter') {
            editingChapterId.value = item.id;
            editChapterModalVisible.value = true;
        } else if (type === 'task') {
            editingTaskId.value = item.id;
            editTaskModalVisible.value = true;
        } else if (type === 'step') {
            editingStepId.value = item.id;
            editStepModalVisible.value = true;
        } else {
            message.info(`${type} 編輯功能開發中`);
        }
    } catch (error) {
        handleHttpError(error, '載入編輯資料');
        showErrorMessage('載入編輯資料失敗');
    }
};

const handleDuplicate = async (item, type) => {
    const typeNames = {
        mission: '任務',
        chapter: '章節',
        task: '任務',
        step: '步驟'
    };

    const typeName = typeNames[type] || type;

    Modal.confirm({
        title: '確認複製',
        content: `確定要複製${typeName}「${item.name}」嗎？`,
        okText: '確定',
        cancelText: '取消',
        onOk: async () => {
            try {
                // 保存當前的展開狀態和選中狀態
                const savedExpandedKeys = [...expandedKeys.value];
                const savedSelectedKeys = [...selectedKeys.value];

                // 找出需要保持展開的父節點 key
                let parentKey = null;
                if (type === 'chapter') {
                    // chapter 的父節點是 mission
                    const mission = missions.value.find(
                        (m) =>
                            m.chapters &&
                            m.chapters.some((ch) => ch.id === item.id)
                    );
                    if (mission) {
                        parentKey = `mission-${mission.id}`;
                    }
                } else if (type === 'task') {
                    // task 的父節點是 chapter
                    for (const mission of missions.value) {
                        for (const chapter of mission.chapters || []) {
                            if (
                                chapter.tasks &&
                                chapter.tasks.some((t) => t.id === item.id)
                            ) {
                                parentKey = `chapter-${chapter.id}`;
                                // 同時需要展開 mission
                                const missionKey = `mission-${mission.id}`;
                                if (!savedExpandedKeys.includes(missionKey)) {
                                    savedExpandedKeys.push(missionKey);
                                }
                                break;
                            }
                        }
                        if (parentKey) break;
                    }
                } else if (type === 'step') {
                    // step 的父節點是 task
                    for (const mission of missions.value) {
                        for (const chapter of mission.chapters || []) {
                            for (const task of chapter.tasks || []) {
                                if (
                                    task.steps &&
                                    task.steps.some((s) => s.id === item.id)
                                ) {
                                    parentKey = `task-${task.id}`;
                                    // 同時需要展開 mission 和 chapter
                                    const missionKey = `mission-${mission.id}`;
                                    const chapterKey = `chapter-${chapter.id}`;
                                    if (
                                        !savedExpandedKeys.includes(missionKey)
                                    ) {
                                        savedExpandedKeys.push(missionKey);
                                    }
                                    if (
                                        !savedExpandedKeys.includes(chapterKey)
                                    ) {
                                        savedExpandedKeys.push(chapterKey);
                                    }
                                    break;
                                }
                            }
                            if (parentKey) break;
                        }
                        if (parentKey) break;
                    }
                }

                // 確保父節點在展開列表中
                if (parentKey && !savedExpandedKeys.includes(parentKey)) {
                    savedExpandedKeys.push(parentKey);
                }

                await copyNode(item.id);
                message.success(`複製${typeName}成功`);

                // 重新載入資料（保持當前頁碼）
                await loadMissions(currentPage.value);

                // 恢復展開狀態
                nextTick(() => {
                    expandedKeys.value = savedExpandedKeys;

                    // 確保所有展開的節點都被載入子資料
                    const loadPromises = savedExpandedKeys.map((key) =>
                        loadNodeChildren(key)
                    );
                    Promise.all(loadPromises).then(() => {
                        // 重新初始化排序
                        nextTick(() => {
                            initTreeSortable();
                        });
                    });
                });
            } catch (error) {
                handleHttpError(error, `複製${typeName}`);
                showErrorMessage(`複製${typeName}失敗`);
            }
        }
    });
};

const handleDelete = (item, type) => {
    const typeNames = {
        mission: '主線',
        chapter: '章節',
        task: '任務項目',
        step: '步驟'
    };
    const typeName = typeNames[type] || '項目';

    Modal.confirm({
        title: '確認刪除',
        content: `確定要刪除${typeName}「${item.name}」嗎？`,
        okText: '確定',
        cancelText: '取消',
        okType: 'danger',
        onOk: async () => {
            try {
                // 保存當前的展開狀態和選中狀態
                const savedExpandedKeys = [...expandedKeys.value];
                const savedSelectedKeys = [...selectedKeys.value];

                // 找出需要保持展開的父節點 key
                let parentKey = null;
                if (type === 'chapter') {
                    // chapter 的父節點是 mission
                    const mission = missions.value.find(
                        (m) =>
                            m.chapters &&
                            m.chapters.some((ch) => ch.id === item.id)
                    );
                    if (mission) {
                        parentKey = `mission-${mission.id}`;
                    }
                } else if (type === 'task') {
                    // task 的父節點是 chapter
                    for (const mission of missions.value) {
                        for (const chapter of mission.chapters || []) {
                            if (
                                chapter.tasks &&
                                chapter.tasks.some((t) => t.id === item.id)
                            ) {
                                parentKey = `chapter-${chapter.id}`;
                                // 同時需要展開 mission
                                const missionKey = `mission-${mission.id}`;
                                if (!savedExpandedKeys.includes(missionKey)) {
                                    savedExpandedKeys.push(missionKey);
                                }
                                break;
                            }
                        }
                        if (parentKey) break;
                    }
                } else if (type === 'step') {
                    // step 的父節點是 task
                    for (const mission of missions.value) {
                        for (const chapter of mission.chapters || []) {
                            for (const task of chapter.tasks || []) {
                                if (
                                    task.steps &&
                                    task.steps.some((s) => s.id === item.id)
                                ) {
                                    parentKey = `task-${task.id}`;
                                    // 同時需要展開 mission 和 chapter
                                    const missionKey = `mission-${mission.id}`;
                                    const chapterKey = `chapter-${chapter.id}`;
                                    if (
                                        !savedExpandedKeys.includes(missionKey)
                                    ) {
                                        savedExpandedKeys.push(missionKey);
                                    }
                                    if (
                                        !savedExpandedKeys.includes(chapterKey)
                                    ) {
                                        savedExpandedKeys.push(chapterKey);
                                    }
                                    break;
                                }
                            }
                            if (parentKey) break;
                        }
                        if (parentKey) break;
                    }
                }

                // 確保父節點在展開列表中
                if (parentKey && !savedExpandedKeys.includes(parentKey)) {
                    savedExpandedKeys.push(parentKey);
                }

                // 從 expandedKeys 中移除被刪除的節點及其子節點
                const itemKey = `${type}-${item.id}`;
                const filteredExpandedKeys = savedExpandedKeys.filter((key) => {
                    // 移除被刪除的節點本身
                    if (key === itemKey) return false;
                    // 移除被刪除節點的子節點（如果有的話）
                    if (
                        type === 'mission' &&
                        key.startsWith(`mission-${item.id}-`)
                    )
                        return false;
                    if (
                        type === 'chapter' &&
                        key.startsWith(`chapter-${item.id}-`)
                    )
                        return false;
                    if (type === 'task' && key.startsWith(`task-${item.id}-`))
                        return false;
                    return true;
                });

                // 從 selectedKeys 中移除被刪除的節點
                const filteredSelectedKeys = savedSelectedKeys.filter(
                    (key) => key !== itemKey
                );

                switch (type) {
                    case 'mission':
                        await deleteMission(item.id);
                        break;
                    case 'chapter':
                        await deleteChapter(item.id);
                        break;
                    case 'task':
                        await deleteTask(item.id);
                        break;
                    case 'step':
                        await deleteStep(item.id);
                        break;
                    default:
                        showErrorMessage(`不支援的類型: ${type}`);
                        return;
                }
                message.success('刪除成功');

                // L4：刪除後清空右側詳情，避免殘留已刪節點
                if (
                    selectedNode.value &&
                    selectedNode.value.type === type &&
                    selectedNode.value.data?.id === item.id
                ) {
                    selectedNode.value = null;
                }

                // 重新載入資料（保持當前頁碼，但如果當前頁沒有資料則回到上一頁）
                const pageToLoad = currentPage.value;
                await loadMissions(pageToLoad);

                // 恢復展開狀態（過濾後的）
                nextTick(() => {
                    expandedKeys.value = filteredExpandedKeys;
                    selectedKeys.value = filteredSelectedKeys;

                    // 確保所有展開的節點都被載入子資料
                    const loadPromises = filteredExpandedKeys.map((key) =>
                        loadNodeChildren(key)
                    );
                    Promise.all(loadPromises).then(() => {
                        // 重新初始化排序
                        nextTick(() => {
                            initTreeSortable();
                        });
                    });
                });
            } catch (error) {
                handleHttpError(error, `刪除${typeName}`);
                showErrorMessage('刪除失敗');
            }
        }
    });
};

// 新增章節時使用的 missionId（臨時存儲）
const newChapterMissionId = ref(null);

const handleAddChapter = async (missionId) => {
    editingChapterId.value = null;
    newChapterMissionId.value = missionId;
    editChapterModalVisible.value = true;
};

// 新增任務時使用的 chapterId（臨時存儲）
const newTaskChapterId = ref(null);

const handleAddTask = async (chapterId) => {
    editingTaskId.value = null;
    newTaskChapterId.value = chapterId;
    editTaskModalVisible.value = true;
};

// 新增步驟時使用的 taskId（臨時存儲）
const newStepTaskId = ref(null);

const handleAddStep = async (taskId) => {
    editingStepId.value = null;
    newStepTaskId.value = taskId;
    editStepModalVisible.value = true;
};

// 初始化任務排序
const initMissionsSortable = () => {
    // 如果沒有編輯權限，不初始化排序功能
    if (!canEdit.value) {
        return;
    }

    if (missionsSortableInstance) {
        missionsSortableInstance.destroy();
    }

    nextTick(() => {
        const container = missionsContainerRef.value;
        if (!container) return;

        missionsSortableInstance = Sortable.create(container, {
            animation: 150,
            ghostClass: 'sortable-ghost',
            handle: '.drag-handle',
            forceFallback: true, // 使用 fallback 模式以支援網格佈局
            fallbackOnBody: true,
            swapThreshold: 0.65, // 網格佈局需要更高的交換閾值
            onEnd: async (evt) => {
                const { oldIndex, newIndex } = evt;

                if (oldIndex === newIndex) return;

                // 在移動數組前取得要交換的兩個 mission
                const firstMission = missions.value[oldIndex];
                const secondMission = missions.value[newIndex];

                if (!firstMission || !secondMission) return;

                // 恢復 DOM 順序
                evt.from.insertBefore(evt.item, evt.from.children[oldIndex]);

                // 移動數組元素
                const movedMission = missions.value.splice(oldIndex, 1)[0];
                missions.value.splice(newIndex, 0, movedMission);

                // 使用統一的 swap-sequence API 交換位置
                try {
                    await swapNodeSequence(
                        'mission',
                        firstMission.id,
                        secondMission.id
                    );
                    message.success('排序更新成功');
                } catch (error) {
                    handleHttpError(error, '更新排序');
                    showErrorMessage('排序更新失敗');
                    // 重新載入以恢復正確順序（保持當前頁碼）
                    await loadMissions(currentPage.value);
                    return;
                }

                // 重新初始化排序
                nextTick(() => {
                    initMissionsSortable();
                });
            }
        });
    });
};

// 篩選變更處理
const handleFilterChange = () => {
    // 篩選時重置到第一頁
    currentPage.value = 1;
    loadMissions(1).then(() => {
        // 篩選後預設收合
        collapseAll();
        // 重新初始化排序
        nextTick(() => {
            initMissionsSortable();
        });
    });
};

// 分頁變更處理
const handlePageChange = (page) => {
    currentPage.value = page;
    loadMissions(page);
};

// 顯示分類管理
const showCategoryManagement = () => {
    categoryManagementVisible.value = true;
};

// 分類管理成功回調
const handleCategoryManagementSuccess = async () => {
    // 重新載入分類列表
    await loadCategories();
};

// 處理從 query 參數來的編輯請求
const handleQueryEdit = async () => {
    const { editTask, editChapter } = route.query;

    if (editTask) {
        try {
            // 在所有 missions 中尋找該 task
            let foundTask = null;
            for (const mission of getMissionHierarchyList()) {
                for (const chapter of mission.chapters || []) {
                    foundTask = (chapter.tasks || []).find(
                        (task) => task.id === Number(editTask)
                    );
                    if (foundTask) break;
                }
                if (foundTask) break;
            }

            if (foundTask) {
                // 如果找到，呼叫 handleEdit（會自動呼叫 getTaskDetail 獲取完整資料）
                await handleEdit(foundTask, 'task');
            } else {
                // 如果找不到，直接呼叫 API 獲取完整資料
                try {
                    const response = await getTaskDetail(Number(editTask));
                    const taskData = response.data?.data || response.data;
                    if (taskData) {
                        await handleEdit(taskData, 'task');
                    } else {
                        showErrorMessage('找不到該任務');
                    }
                } catch (error) {
                    handleHttpError(error, '載入任務詳情');
                    showErrorMessage('載入任務詳情失敗');
                }
            }

            // 清除 query 參數
            router.replace({ query: {} });
        } catch (error) {
            console.error('處理編輯任務請求失敗:', error);
        }
    } else if (editChapter) {
        try {
            // 在所有 missions 中尋找該 chapter
            let foundChapter = null;
            for (const mission of getMissionHierarchyList()) {
                foundChapter = (mission.chapters || []).find(
                    (chapter) => chapter.id === Number(editChapter)
                );
                if (foundChapter) break;
            }

            if (foundChapter) {
                // 如果找到，呼叫 handleEdit
                await handleEdit(foundChapter, 'chapter');
            } else {
                // 如果找不到，直接呼叫 API 獲取完整資料
                try {
                    const response = await getChapterDetail(
                        Number(editChapter)
                    );
                    const chapterData = response.data?.data || response.data;
                    if (chapterData) {
                        await handleEdit(chapterData, 'chapter');
                    } else {
                        showErrorMessage('找不到該章節');
                    }
                } catch (error) {
                    handleHttpError(error, '載入章節詳情');
                    showErrorMessage('載入章節詳情失敗');
                }
            }

            // 清除 query 參數
            router.replace({ query: {} });
        } catch (error) {
            console.error('處理編輯章節請求失敗:', error);
        }
    }
};

// 生命週期
onMounted(() => {
    loadCategories();
    loadMissionOptions();
    loadMissions(1).then(() => {
        // 預設所有 mission 都收合，避免畫面過於複雜
        collapseAll();
        // 初始化任務排序
        nextTick(() => {
            initMissionsSortable();
            // 處理 query 參數的編輯請求
            handleQueryEdit();
        });
    });
});

onUnmounted(() => {
    if (missionsSortableInstance) {
        missionsSortableInstance.destroy();
    }
    // 清除所有樹狀選單的 sortable 實例
    treeSortableInstances.forEach((instance) => {
        if (instance) instance.destroy();
    });
    treeSortableInstances.clear();
});
</script>

<style scoped>
.page {
    padding: 32px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95),
        rgba(255, 255, 255, 0.98)
    );
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
}

/* 背景裝飾網格 */
.page::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
        linear-gradient(rgba(42, 74, 122, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(42, 74, 122, 0.03) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
    z-index: 0;
}

.page-header {
    margin-bottom: 24px;
    position: relative;
    z-index: 1;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    min-height: 400px;
    position: relative;
    box-shadow:
        0 0 40px rgba(42, 74, 122, 0.2),
        0 10px 40px rgba(0, 0, 0, 0.05);
    backdrop-filter: blur(10px);
    z-index: 1;
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    position: relative;
    z-index: 2;
    flex-wrap: wrap;
    gap: 16px;
    padding: 20px 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 16px;
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 12px;
}

.toolbar-right {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 12px;
}

.search-box {
    flex: 1;
    min-width: 300px;
}

.loading-container,
.empty-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 500px;
    position: relative;
    z-index: 1;
    flex-direction: column;
    gap: 16px;
}

.loading-container {
    animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* 自定義載入動畫 */
.custom-spinner {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
}

.spinner-ring {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5);
    animation: spinnerPulse 1.4s ease-in-out infinite;
    box-shadow: 0 0 10px rgba(42, 74, 122, 0.5);
}

.spinner-ring:nth-child(1) {
    animation-delay: 0s;
}

.spinner-ring:nth-child(2) {
    animation-delay: 0.2s;
}

.spinner-ring:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes spinnerPulse {
    0%,
    100% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(0.5);
        opacity: 0.5;
    }
}

.loading-text {
    margin-top: 16px;
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    letter-spacing: 0.5px;
    animation: textPulse 2s ease-in-out infinite;
}

@keyframes textPulse {
    0%,
    100% {
        opacity: 0.6;
    }

    50% {
        opacity: 1;
    }
}

.empty-container {
    animation: fadeIn 0.5s ease-in;
}

.empty-icon {
    margin-bottom: 20px;
    animation: iconFloat 3s ease-in-out infinite;
    filter: drop-shadow(0 0 10px rgba(42, 74, 122, 0.3));
}

@keyframes iconFloat {
    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-10px);
    }
}

.empty-container :deep(.ant-empty) {
    padding: 20px;
}

.empty-container :deep(.ant-empty-description) {
    color: #8a8a9a;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    letter-spacing: 0.3px;
}

/* 主佈局：左側樹狀選單 + 右側詳情面板 */
.main-layout {
    display: flex;
    gap: 0;
    height: calc(100vh - 240px);
    min-height: 600px;
    position: relative;
    z-index: 1;
}

/* 左側樹狀選單面板 */
.tree-panel {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    );
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
}

.tree-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.2);
    background: linear-gradient(
        135deg,
        rgba(255, 158, 207, 0.05),
        rgba(42, 74, 122, 0.05)
    );
}

.tree-panel-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.tree-panel-content {
    flex: 1;
    overflow: hidden;
    padding: 12px;
    display: flex;
    flex-direction: column;
}

.tree-content-wrapper {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
}

.tree-pagination {
    padding: 16px 12px;
    border-top: 1px solid rgba(42, 74, 122, 0.2);
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    flex-shrink: 0;
}

.tree-pagination :deep(.ant-pagination) {
    margin: 0;
    text-align: center;
}

.tree-pagination :deep(.ant-pagination-total-text) {
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    font-size: 13px;
}

/* 樹狀選單樣式 */
:deep(.mission-tree) {
    background: transparent;
    font-family: 'Courier New', monospace;
}

:deep(.mission-tree .ant-tree-node-content-wrapper) {
    padding: 6px 8px;
    border-radius: 6px;
    transition: all 0.2s ease;
    user-select: none;
    position: relative;
    /* 確保拖曳手柄的 z-index 生效 */
}

:deep(.mission-tree .ant-tree-treenode) {
    user-select: none;
}

:deep(.mission-tree .ant-tree-node-content-wrapper:hover) {
    background: rgba(42, 74, 122, 0.1);
}

:deep(.mission-tree .ant-tree-node-selected .ant-tree-node-content-wrapper) {
    background: linear-gradient(
        90deg,
        rgba(42, 74, 122, 0.2),
        rgba(138, 175, 255, 0.2)
    ) !important;
    border: 1px solid rgba(42, 74, 122, 0.4);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2);
}

:deep(.mission-tree .ant-tree-switcher) {
    color: #2a4a7a;
}

:deep(.mission-tree .ant-tree-switcher:hover) {
    color: #3b6ea5;
}

/* 隱藏展開時的 switcher（因為我們用排序按鈕替代） */
:deep(.mission-tree .ant-tree-switcher-open) {
    display: none !important;
}

/* 保留收合時的 switcher */
:deep(.mission-tree .ant-tree-switcher-close) {
    display: inline-flex !important;
}

.tree-node-title {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    width: 100%;
    min-width: 0;
}

.drag-handle-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    color: rgba(42, 74, 122, 0.4);
    cursor: move;
    cursor: grab;
    transition: all 0.2s ease;
    flex-shrink: 0;
    margin-right: 8px;
    user-select: none;
    -webkit-user-select: none;
    touch-action: none;
    pointer-events: auto;
    /* 確保可以接收事件 */
    position: relative;
    z-index: 10;
    /* 確保在最上層 */
}

.drag-handle-icon:active {
    cursor: grabbing;
    color: #2a4a7a;
}

.drag-handle-icon:hover {
    color: #2a4a7a;
    transform: scale(1.1);
}

.drag-handle-icon svg {
    width: 100%;
    height: 100%;
    pointer-events: none;
}

/* 只有 step 類型顯示勾選圖標 */
.node-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-right: 8px;
    transition: all 0.2s ease;
}

.node-icon.step svg {
    width: 100%;
    height: 100%;
    filter: drop-shadow(0 0 3px rgba(255, 158, 207, 0.4));
    transition: all 0.2s ease;
}

.node-icon.step:hover svg {
    transform: scale(1.1);
    filter: drop-shadow(0 0 5px rgba(255, 158, 207, 0.6));
}

.node-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 8px;
}

.node-loading :deep(.ant-spin) {
    color: #2a4a7a;
}

.node-loading :deep(.ant-spin-dot-item) {
    background-color: #2a4a7a;
}

.node-name {
    flex: 1;
    font-size: 14px;
    color: #2a2a3a;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.node-category-tag,
.node-status-tag {
    margin: 0;
    font-size: 11px;
    padding: 0 6px;
    height: 20px;
    line-height: 20px;
}

/* 排序按鈕區域 */
.node-sort-actions {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    flex-shrink: 0;
}

.sort-btn {
    width: 24px;
    height: 24px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.2s ease;
    color: rgba(42, 74, 122, 0.6);
}

.sort-btn:hover {
    background: rgba(42, 74, 122, 0.1);
    color: #2a4a7a;
    transform: scale(1.1);
}

.sort-btn-up:hover {
    color: #3b6ea5;
    background: rgba(138, 175, 255, 0.1);
}

.sort-btn-down:hover {
    color: #0e7490;
    background: rgba(168, 255, 246, 0.1);
}

.sort-btn-cancel:hover {
    color: #ffa87a;
    background: rgba(255, 168, 122, 0.1);
}

.sort-btn-toggle:hover {
    color: #2a4a7a;
    background: rgba(42, 74, 122, 0.15);
}

.sort-btn :deep(.anticon) {
    font-size: 12px;
}

/* 全局排序模式按鈕 */
.sort-mode-active {
    background: rgba(42, 74, 122, 0.15) !important;
    color: #2a4a7a !important;
}

.sort-mode-active:hover {
    background: rgba(42, 74, 122, 0.25) !important;
    color: #1a4268 !important;
}

/* 拖曳調整寬度的分隔線 */
.resizer {
    width: 8px;
    background: rgba(42, 74, 122, 0.1);
    cursor: col-resize;
    position: relative;
    transition: background 0.2s ease;
    flex-shrink: 0;
}

.resizer:hover,
.resizer.resizing {
    background: rgba(42, 74, 122, 0.3);
}

.resizer::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 2px;
    height: 40px;
    background: rgba(42, 74, 122, 0.4);
    border-radius: 1px;
}

.resizer:hover::before,
.resizer.resizing::before {
    background: rgba(42, 74, 122, 0.8);
    width: 3px;
}

/* 右側詳情面板 */
.detail-panel {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    );
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 16px;
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.1);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
}

.detail-panel-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 40px;
}

.detail-panel-empty .empty-icon {
    margin-bottom: 24px;
    animation: iconFloat 3s ease-in-out infinite;
    filter: drop-shadow(0 0 10px rgba(42, 74, 122, 0.3));
}

.detail-panel-empty .empty-text {
    color: #8a8a9a;
    font-family: 'Courier New', monospace;
    font-size: 16px;
    letter-spacing: 0.5px;
}

.detail-panel-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
}

.detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.2);
    background: linear-gradient(
        135deg,
        rgba(255, 158, 207, 0.05),
        rgba(42, 74, 122, 0.05)
    );
    flex-shrink: 0;
}

.detail-title {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
}

.detail-icon {
    display: flex;
    align-items: center;
    font-size: 24px;
    flex-shrink: 0;
}

.detail-icon.mission {
    color: #2a4a7a;
}

.detail-icon.chapter {
    color: #3b6ea5;
}

.detail-icon.task {
    color: #0e7490;
}

.detail-icon.step {
    color: #64748b;
}

.detail-title h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.detail-actions {
    flex-shrink: 0;
}

.detail-body {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
}

/* 詳情區塊樣式 */
.detail-sections {
    display: flex;
    flex-direction: column;
    gap: 0;
}

.detail-section {
    padding: 20px 0;
}

.detail-section:first-child {
    padding-top: 0;
}

.section-divider {
    height: 1px;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(42, 74, 122, 0.3),
        transparent
    );
    margin: 24px 0;
}

.section-header {
    font-size: 16px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin: 0 0 16px 0;
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.detail-section-content {
    padding: 0;
}

/* 重複設置同一行顯示 */
.repeat-settings-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    align-items: flex-start;
}

.detail-field-inline {
    flex: 0 0 auto;
    min-width: 120px;
}

.detail-field-inline .field-label {
    margin-bottom: 6px;
}

.detail-field-inline .field-value {
    margin-bottom: 0;
}

/* 詳情欄位樣式 */
.detail-field {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.1);
}

.detail-field:last-child {
    border-bottom: none;
    margin-bottom: 0;
}

.field-label {
    font-size: 12px;
    font-weight: 600;
    color: #8a8a9a;
    font-family: 'Courier New', monospace;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.field-value {
    font-size: 14px;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    line-height: 1.6;
    word-break: break-word;
}

/* 規則項目樣式 */
.rule-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    margin-bottom: 8px;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.rule-item:last-child {
    margin-bottom: 0;
}

.rule-item:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: rgba(42, 74, 122, 0.4);
    box-shadow: 0 2px 4px rgba(42, 74, 122, 0.1);
}

.rule-ref {
    font-size: 13px;
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
    flex: 1;
}

/* 獎勵項目樣式 */
.reward-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 14px;
    margin-bottom: 12px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.7),
        rgba(248, 249, 250, 0.6)
    );
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 10px;
    transition: all 0.2s ease;
}

.reward-item:last-child {
    margin-bottom: 0;
}

.reward-item:hover {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9),
        rgba(248, 249, 250, 0.8)
    );
    border-color: rgba(42, 74, 122, 0.4);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.15);
    transform: translateX(2px);
}

.reward-type {
    flex-shrink: 0;
}

.reward-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.reward-ref,
.reward-amount {
    font-size: 13px;
    color: #6a6a7a;
    font-family: 'Courier New', monospace;
}

.reward-amount {
    font-weight: 600;
    color: #2a2a3a;
}

/* 響應式設計 */
@media (max-width: 1200px) {
    .main-layout {
        flex-direction: column;
        height: auto;
        min-height: auto;
    }

    .tree-panel {
        width: 100% !important;
        height: 400px;
        margin-bottom: 16px;
    }

    .detail-panel {
        width: 100% !important;
        min-height: 500px;
    }

    .resizer {
        display: none;
    }
}

/* 響應式設計 */
@media (max-width: 1400px) {
    .missions-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 24px;
    }

    .content {
        padding: 32px;
    }
}

@media (max-width: 768px) {
    .page {
        padding: 20px;
    }

    .page-header h1 {
        font-size: 24px;
    }

    .content {
        padding: 24px;
        border-radius: 16px;
    }

    .toolbar {
        padding: 16px;
        flex-direction: column;
        align-items: stretch;
    }

    .toolbar-left,
    .toolbar-right {
        width: 100%;
        justify-content: space-between;
    }

    .missions-grid {
        grid-template-columns: 1fr;
        gap: 20px;
    }
}

/* 拖曳排序樣式 */
:deep(.sortable-ghost) {
    opacity: 0.5;
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.15),
        rgba(138, 175, 255, 0.15)
    ) !important;
    border: 2px dashed rgba(42, 74, 122, 0.5) !important;
    box-shadow: 0 0 20px rgba(42, 74, 122, 0.3) !important;
    transform: scale(0.98);
}

/* 按鈕樣式調整 */
:deep(.toolbar .ant-btn-default) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    color: #2a2a3a !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow: 0 2px 4px rgba(42, 74, 122, 0.1) !important;
    height: 36px !important;
    padding: 0 16px !important;
}

:deep(.toolbar .ant-btn-default:hover) {
    border-color: rgba(138, 175, 255, 0.6) !important;
    color: #3b6ea5 !important;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 1),
        rgba(248, 249, 250, 0.95)
    ) !important;
    box-shadow:
        0 4px 12px rgba(138, 175, 255, 0.3),
        inset 0 0 10px rgba(138, 175, 255, 0.1) !important;
    transform: translateY(-2px);
}

:deep(.toolbar .ant-btn-default:active) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(138, 175, 255, 0.2) !important;
}

:deep(.toolbar .ant-btn-default .anticon) {
    transition: all 0.3s ease !important;
}

:deep(.toolbar .ant-btn-default:hover .anticon) {
    filter: drop-shadow(0 0 4px rgba(138, 175, 255, 0.6));
    transform: scale(1.1);
}

:deep(.toolbar .ant-btn-primary) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    color: #ffffff !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    box-shadow:
        0 4px 12px rgba(42, 74, 122, 0.4),
        inset 0 0 10px rgba(255, 255, 255, 0.1) !important;
    height: 36px !important;
    padding: 0 20px !important;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

:deep(.toolbar .ant-btn-primary:hover) {
    background: linear-gradient(45deg, #1a4268, #3b6ea5) !important;
    box-shadow:
        0 6px 20px rgba(42, 74, 122, 0.6),
        inset 0 0 15px rgba(255, 255, 255, 0.15) !important;
    transform: translateY(-2px);
}

:deep(.toolbar .ant-btn-primary:active) {
    transform: translateY(0);
    box-shadow:
        0 2px 8px rgba(42, 74, 122, 0.4),
        inset 0 0 8px rgba(255, 255, 255, 0.1) !important;
}

/* Vaporwave 風格新增按鈕 */
:deep(.vaporwave-add-btn) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    color: #ffffff !important;
    border-radius: 10px !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.4),
        inset 0 0 12px rgba(255, 255, 255, 0.1) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
    position: relative;
    overflow: hidden;
}

:deep(.vaporwave-add-btn::before) {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
    );
    transition: left 0.5s ease;
}

:deep(.vaporwave-add-btn:hover::before) {
    left: 100%;
}

:deep(.vaporwave-add-btn:hover) {
    background: linear-gradient(45deg, #1a4268, #3b6ea5) !important;
    box-shadow:
        0 6px 24px rgba(42, 74, 122, 0.6),
        inset 0 0 15px rgba(255, 255, 255, 0.15) !important;
    transform: translateY(-2px) scale(1.02);
}

:deep(.vaporwave-add-btn:active) {
    transform: translateY(0) scale(1);
    box-shadow:
        0 2px 10px rgba(42, 74, 122, 0.4),
        inset 0 0 8px rgba(255, 255, 255, 0.1) !important;
}

/* 下拉選單樣式優化 */
:deep(.toolbar .ant-select) {
    font-family: 'Courier New', monospace !important;
}

/* 分類管理按鈕樣式 */
.category-management-btn {
    color: rgba(42, 74, 122, 0.6) !important;
    border: none !important;
    box-shadow: none !important;
    padding: 4px 8px !important;
    transition: all 0.2s ease !important;
}

.category-management-btn:hover {
    color: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.1) !important;
}

:deep(.toolbar .ant-select-selector) {
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 8px !important;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    transition: all 0.3s ease !important;
    box-shadow: 0 2px 4px rgba(42, 74, 122, 0.1) !important;
}

:deep(.toolbar .ant-select:hover .ant-select-selector) {
    border-color: rgba(138, 175, 255, 0.6) !important;
    box-shadow: 0 4px 12px rgba(138, 175, 255, 0.2) !important;
}

:deep(.toolbar .ant-select-focused .ant-select-selector) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow:
        0 0 0 2px rgba(42, 74, 122, 0.2),
        0 4px 12px rgba(42, 74, 122, 0.3) !important;
}

/* 獎勵設定樣式 */
.rewards-section {
    margin-top: 8px;
}

.rewards-header {
    margin-bottom: 12px;
    display: flex;
    justify-content: flex-end;
}

.rewards-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.reward-item {
    padding: 12px;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.reward-item:hover {
    border-color: rgba(42, 74, 122, 0.4);
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 2px 4px rgba(42, 74, 122, 0.1);
}

.no-rewards {
    padding: 4px 8px;
    text-align: center;
    color: #8a8a9a;
    font-size: 13px;
    font-family: 'Courier New', monospace;
    min-height: auto;
}

/* 規則區塊樣式 */
.rules-section {
    margin-top: 4px;
}

.rules-header {
    margin-bottom: 8px;
    display: flex;
    justify-content: flex-end;
}

.rules-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.rule-item {
    padding: 8px;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 6px;
    transition: all 0.2s ease;
}

.rule-item:hover {
    border-color: rgba(42, 74, 122, 0.4);
    background: rgba(255, 255, 255, 0.8);
    box-shadow: 0 2px 4px rgba(42, 74, 122, 0.1);
}

.no-rules {
    padding: 4px 8px;
    text-align: center;
    color: #8a8a9a;
    font-size: 13px;
    font-family: 'Courier New', monospace;
    min-height: auto;
}

/* 規則刪除按鈕樣式 */
.delete-rule-btn {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
}

.delete-rule-btn :deep(.anticon) {
    color: #ff4d4f !important;
    font-size: 16px;
}

.delete-rule-btn:hover :deep(.anticon) {
    color: #ff7875 !important;
}

.delete-rule-btn:focus {
    border: none !important;
    box-shadow: none !important;
}

/* Switch 圓形樣式 */
:deep(.ant-switch) {
    border-radius: 20px !important;
}

:deep(.ant-switch-handle) {
    border-radius: 50% !important;
}

:deep(.ant-switch-handle::before) {
    border-radius: 50% !important;
}

/* 表單區塊卡片樣式 */
.form-section-card {
    margin-bottom: 16px;
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(42, 74, 122, 0.2) !important;
    border-radius: 12px !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1) !important;
}

.form-section-card :deep(.ant-card-head) {
    background: linear-gradient(
        135deg,
        rgba(255, 158, 207, 0.1),
        rgba(42, 74, 122, 0.1)
    ) !important;
    border-bottom: 1px solid rgba(42, 74, 122, 0.2) !important;
    border-radius: 12px 12px 0 0 !important;
    padding: 12px 16px !important;
}

.form-section-card :deep(.ant-card-head-title) {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 15px !important;
}

.form-section-card :deep(.ant-card-body) {
    padding: 20px !important;
}

/* 表單區塊 Collapse 樣式 */
.form-sections-collapse {
    background: transparent !important;
}

.form-sections-collapse :deep(.ant-collapse-item) {
    background: transparent !important;
    border: none !important;
    margin-bottom: 8px !important;
}

.form-sections-collapse :deep(.ant-collapse-header) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(42, 74, 122, 0.2) !important;
    border-radius: 12px !important;
    padding: 12px 16px !important;
    transition: all 0.3s ease !important;
}

.form-sections-collapse :deep(.ant-collapse-header:hover) {
    background: rgba(255, 255, 255, 1) !important;
    border-color: rgba(42, 74, 122, 0.4) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.form-sections-collapse :deep(.ant-collapse-header:focus) {
    outline: none !important;
}

.form-sections-collapse :deep(.ant-collapse-content) {
    background: transparent !important;
    border: none !important;
    border-radius: 0 0 12px 12px !important;
    padding: 0 !important;
}

.form-sections-collapse :deep(.ant-collapse-content-box) {
    padding: 0 !important;
}

.form-sections-collapse :deep(.ant-collapse-expand-icon) {
    color: #2a4a7a !important;
    font-size: 14px !important;
}

.form-sections-collapse :deep(.ant-collapse-expand-icon:hover) {
    color: #3b6ea5 !important;
}

.section-header {
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 15px !important;
}

.section-note {
    font-weight: 400;
    font-size: 12px;
    color: #8a8a9a;
    margin-left: 4px;
}

.form-section-card-inner {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(42, 74, 122, 0.2) !important;
    border-radius: 8px !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1) !important;
    margin-top: 4px !important;
}

.form-section-card-inner :deep(.ant-card-body) {
    padding: 12px !important;
}

.form-section-card-inner :deep(.ant-form-item) {
    margin-bottom: 8px !important;
}

.form-section-card-inner :deep(.ant-form-item:last-child) {
    margin-bottom: 0 !important;
}

/* 空狀態時減少 padding */
.form-section-card-inner:has(.no-rules) :deep(.ant-card-body),
.form-section-card-inner:has(.no-rewards) :deep(.ant-card-body) {
    padding: 4px 12px !important;
}

.section-subtitle {
    font-size: 14px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.2);
}

/* 這些樣式已移至 NodeSelectorModal 組件 */

/* 表單區塊分隔線樣式 */
.section-divider {
    margin: 20px 0 16px 0 !important;
    border-color: rgba(42, 74, 122, 0.3) !important;
}

.section-divider :deep(.ant-divider-inner-text) {
    padding: 0 12px;
}

.divider-text {
    font-family: 'Courier New', monospace;
    font-size: 13px;
    font-weight: 600;
    color: #2a4a7a;
    text-shadow: 0 0 8px rgba(42, 74, 122, 0.3);
    letter-spacing: 0.5px;
}

/* 精簡表單行樣式 */
.form-row-compact {
    margin-bottom: 12px;
}

.form-row-compact :deep(.ant-form-item) {
    margin-bottom: 12px;
}

.form-row-compact :deep(.ant-form-item-label) {
    padding-bottom: 4px;
}

/* 協作角色下拉選單樣式 */
:deep(.ant-select-dropdown) {
    max-height: 300px !important;
    overflow-y: auto !important;
}
</style>
